import React, { useState, useEffect } from "react";
import "./InitialData.css";
import VectorIcon from "../../../../images/projects/Vector.svg";
import PyramidIcon from "../../../../images/projects/Verified Badge.png";
import BuildingIcon from "../../../../images/projects/Condo.png";
import CubesIcon from "../../../../images/projects/Sugar Cubes.png";
import ParkingIcon from "../../../../images/projects/ProjectFinance/parking.svg";
import Select from "react-select";
import { projectEstimatesApi } from "../../../../api/projectEstimatesApi";
import { projectApi, getAllBlocks, addBlock } from "../../../../api/projectApi";
import { reportsApi } from "../../../../api/reportsApi";
import { useCurrentUser } from "../../../../contexts/CurrentUserContext";
import { useOvertime } from "../../../../contexts/OvertimeContext";
import { usePendingReports } from "../../../../contexts/PendingReportsContext";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { getAPIURL } from "../../../../api/config";
import {
  getCpTeiInfTypes,
  getCpTeiEngrooms,
  getCpTeis,
  calculateTotalArea,
  addCpTei,
  updateCpTei,
  deleteCpTei,
  uploadProjectImage,
  saveProjectBlocks,
  restoreVersion,
} from "../../../../api/projectEstimatesApi";
import { getProjectApprovalStatuses } from "../../../../api/projectApprovalApi";
import Preloader from "../../../Preloader/Preloader";

// Функция для безопасного вычисления математических выражений
// Функция для валидации числового значения
const parseNumericValue = (value) => {
  // Если значение уже число, возвращаем его
  if (typeof value === "number") {
    return isNaN(value) || !isFinite(value) ? 0 : value;
  }

  // Если значение не строка или пустое, возвращаем 0
  if (!value || typeof value !== "string") {
    return 0;
  }

  // Убираем пробелы и заменяем запятую на точку
  const cleanValue = value.trim().replace(",", ".");

  // Если пустая строка после очистки, возвращаем 0
  if (cleanValue === "" || cleanValue === "-") {
    return 0;
  }

  // Проверяем, что это число (целое или с десятичной частью)
  if (!/^-?\d+\.?\d*$/.test(cleanValue)) {
    return 0;
  }

  const parsed = parseFloat(cleanValue);
  return isNaN(parsed) || !isFinite(parsed) ? 0 : parsed;
};

// Функция для умного округления: до сотых, но до десятых если в конце 0
const smartRound = (value) => {
  const rounded = Math.round(value * 100) / 100;
  // Если после округления до сотых последняя цифра 0, округляем до десятых
  if (rounded % 0.1 === 0) {
    return Math.round(rounded * 10) / 10;
  }
  return rounded;
};

// Глобальный inline-редактор в стиле WorkTab/QualityTab
const InlineEditable = ({
  value,
  onSave,
  width = 120,
  inputProps = {},
  isEditable = true,
}) => {
  const effectiveIsEditable = isEditable;
  const [isEditing, setIsEditing] = React.useState(false);
  const [localValue, setLocalValue] = React.useState(value);
  const inputRef = React.useRef(null);
  const computeWidthPx = (val) => {
    const text =
      val === undefined || val === null || val === "" ? "0" : String(val);
    const charCount = text.length;
    return Math.max(30, charCount * 10 + 16);
  };

  React.useEffect(() => {
    setLocalValue(value);
  }, [value]);

  React.useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      const input = inputRef.current;
      requestAnimationFrame(() => {
        try {
          input.select();
        } catch {}
      });
    }
  }, [isEditing]);

  if (isEditing) {
    const wContent = computeWidthPx(localValue);
    const w = Math.max(width, wContent);
    return (
      <div
        style={{
          position: "relative",
          width: `${w}px`,
          margin: 0,
          display: "inline-block",
        }}
      >
        <input
          {...{
            type: "number",
            step: "0.01",
            min: "0",
            inputMode: "decimal",
            ...inputProps,
          }}
          value={localValue}
          ref={inputRef}
          onChange={(e) => setLocalValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              // Убираем ведущие нули из числовых значений
              const cleanValue =
                localValue === "" ? "0" : String(Number(localValue));
              onSave(cleanValue);
              setIsEditing(false);
            } else if (e.key === "Escape") {
              setLocalValue(value);
              setIsEditing(false);
            }
          }}
          style={{
            width: `${w}px`,
            padding: "6px 10px",
            border: "1.5px dashed #B8C2D1",
            borderRadius: "6px",
            background: "#FFFFFF",
            fontSize: "15px",
            fontFamily: "Manrope, sans-serif",
            textAlign: "left",
            outline: "none",
            transition: "border-color 0.2s",
          }}
          onFocus={(e) => (e.target.style.borderColor = "#007bff")}
          onBlur={(e) => {
            e.target.style.borderColor = "#ccc";
            // Автоматически сохраняем при потере фокуса
            const cleanValue =
              localValue === "" ? "0" : String(Number(localValue));
            onSave(cleanValue);
            setIsEditing(false);
          }}
          disabled={!effectiveIsEditable}
        />
      </div>
    );
  }

  const displayValue = value ?? "0";
  const wViewContent = computeWidthPx(displayValue);
  const wView = Math.max(width, wViewContent);
  return (
    <div
      style={{
        width: `${wView}px`,
        margin: 0,
        textAlign: "left",
        display: "inline-block",
      }}
    >
      <span
        style={{
          fontSize: "15px",
          fontFamily: "Manrope, sans-serif",
          display: "inline-block",
          width: `${wView}px`,
          padding: "6px 10px",
          border: "1.5px dashed #B8C2D1",
          borderRadius: "6px",
          background: !isEditable ? "#F4F6FA" : "#F9FBFF",
          whiteSpace: "nowrap",
          cursor: !isEditable ? "default" : "pointer",
          textAlign: "left",
        }}
        onClick={() => (!isEditable ? undefined : setIsEditing(true))}
      >
        {displayValue}
      </span>
    </div>
  );
};

// InlineEditable с поддержкой математических выражений (как в Excel)
const InlineEditableWithMath = ({
  value,
  onSave,
  width = 120,
  inputProps = {},
  isEditable = true,
}) => {
  const effectiveIsEditable = isEditable;
  const [isEditing, setIsEditing] = React.useState(false);
  const [localValue, setLocalValue] = React.useState(value);
  const [originalExpression, setOriginalExpression] = React.useState(value);
  const inputRef = React.useRef(null);
  const prevValueRef = React.useRef(value);

  const computeWidthPx = (val) => {
    const text =
      val === undefined || val === null || val === "" ? "0" : String(val);
    const charCount = text.length;
    return Math.max(30, charCount * 10 + 16);
  };

  React.useEffect(() => {
    // Обновляем только если значение действительно изменилось
    if (value !== prevValueRef.current) {
      setLocalValue(value);
      setOriginalExpression(value);
      prevValueRef.current = value;
    }
  }, [value]);

  React.useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      const input = inputRef.current;
      requestAnimationFrame(() => {
        try {
          input.select();
        } catch {}
      });
    }
  }, [isEditing]);

  const handleSave = (inputValue) => {
    // Валидируем и сохраняем только числовое значение
    const numericValue = parseNumericValue(inputValue);
    const valueToSave = numericValue.toString();
    setOriginalExpression(valueToSave);
    onSave(valueToSave);
  };

  if (isEditing) {
    const wContent = computeWidthPx(localValue);
    const w = Math.max(width, wContent);
    return (
      <div
        style={{
          position: "relative",
          width: `${w}px`,
          margin: 0,
          display: "inline-block",
        }}
      >
        <input
          {...{
            ...inputProps,
            type: "text",
          }}
          value={localValue}
          ref={inputRef}
          onChange={(e) => {
            // Разрешаем только числа, точку, запятую и минус
            const value = e.target.value;
            if (value === "" || /^-?\d*[.,]?\d*$/.test(value)) {
              setLocalValue(value);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleSave(localValue);
              setIsEditing(false);
            } else if (e.key === "Escape") {
              setLocalValue(originalExpression);
              setIsEditing(false);
            }
          }}
          style={{
            width: `${w}px`,
            padding: "6px 10px",
            border: "1.5px dashed #B8C2D1",
            borderRadius: "6px",
            background: "#FFFFFF",
            fontSize: "15px",
            fontFamily: "Manrope, sans-serif",
            textAlign: "left",
            outline: "none",
            transition: "border-color 0.2s",
          }}
          onFocus={(e) => (e.target.style.borderColor = "#007bff")}
          onBlur={(e) => {
            e.target.style.borderColor = "#ccc";
            // Автоматически сохраняем при потере фокуса
            handleSave(localValue);
            setIsEditing(false);
          }}
          placeholder="Введите число"
          disabled={!effectiveIsEditable}
        />
      </div>
    );
  }

  // Показываем числовое значение
  const displayValue = value ?? "0";

  const wViewContent = computeWidthPx(displayValue);
  const wView = Math.max(width, wViewContent);

  return (
    <div
      style={{
        width: `${wView}px`,
        margin: 0,
        textAlign: "left",
        display: "inline-block",
      }}
    >
      <span
        style={{
          fontSize: "15px",
          fontFamily: "Manrope, sans-serif",
          display: "inline-block",
          width: `${wView}px`,
          padding: "6px 10px",
          border: "1.5px dashed #B8C2D1",
          borderRadius: "6px",
          background: !isEditable ? "#F4F6FA" : "#F9FBFF",
          whiteSpace: "nowrap",
          cursor: !isEditable ? "default" : "pointer",
          textAlign: "left",
        }}
        onClick={() => {
          if (!effectiveIsEditable) return;
          setLocalValue(originalExpression);
          setIsEditing(true);
        }}
      >
        {displayValue}
      </span>
    </div>
  );
};

// Компонент InitialData теперь принимает projectId как пропс

const NumberInput = ({ value, onChange, min = 0 }) => {
  const increment = () => onChange(value + 1);
  const decrement = () => onChange(value > min ? value - 1 : min);

  return (
    <div className="number-input">
      <button type="button" onClick={decrement} disabled={value <= min}>
        -
      </button>
      <input type="number" value={value} readOnly />
      <button type="button" onClick={increment}>
        +
      </button>
    </div>
  );
};

// Компонент выпадающего списка для этажности
const SelectInput = ({
  value,
  onChange,
  options = [],
  minValue = null,
  isEditable = true,
}) => {
  const effectiveIsEditable = isEditable;
  // Фильтруем опции по минимальному значению, если указано
  const filteredOptions = minValue
    ? options.filter((option) => option >= minValue)
    : options;

  return (
    <div className="select-input">
      <select
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value))}
        className="floor-select"
        disabled={!effectiveIsEditable}
      >
        {filteredOptions.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </div>
  );
};

// Компонент селекта со стрелочками и выпадающим списком
const NumberSelect = ({
  value,
  onChange,
  options = [],
  minValue = null,
  maxValue = null,
  isEditable = true,
  placeholder = "Выберите значение",
}) => {
  const effectiveIsEditable = isEditable;
  const [isOpen, setIsOpen] = React.useState(false);
  const containerRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const isClickInsideRef = React.useRef(false);

  // Фильтруем опции по минимальному и максимальному значению
  const filteredOptions = options.filter((option) => {
    if (minValue !== null && option < minValue) return false;
    if (maxValue !== null && option > maxValue) return false;
    return true;
  });

  // Формируем опции для выпадающего списка
  const selectOptions = filteredOptions.map((option) => ({
    value: option,
    label: option.toString(),
  }));

  // Находим текущий индекс в опциях
  const currentIndex = selectOptions.findIndex((opt) => opt.value === value);

  // Обработчик увеличения значения (стрелка вверх)
  const handleIncrement = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!effectiveIsEditable) return;

    if (currentIndex >= 0 && currentIndex < selectOptions.length - 1) {
      // Если текущее значение есть в опциях, берем следующее
      onChange(selectOptions[currentIndex + 1].value);
    } else if (value !== null && value !== undefined) {
      // Если значения нет в опциях, увеличиваем на 1
      const newValue = parseInt(value) + 1;
      if (maxValue === null || newValue <= maxValue) {
        onChange(newValue);
      }
    } else if (selectOptions.length > 0) {
      // Если значения нет, берем первое доступное
      onChange(selectOptions[0].value);
    }
  };

  // Обработчик уменьшения значения (стрелка вниз)
  const handleDecrement = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!effectiveIsEditable) return;

    if (currentIndex > 0) {
      // Если текущее значение есть в опциях, берем предыдущее
      onChange(selectOptions[currentIndex - 1].value);
    } else if (value !== null && value !== undefined) {
      // Если значения нет в опциях, уменьшаем на 1
      const newValue = parseInt(value) - 1;
      if (minValue === null || newValue >= minValue) {
        onChange(newValue);
      }
    } else if (selectOptions.length > 0) {
      // Если значения нет, берем первое доступное
      onChange(selectOptions[0].value);
    }
  };

  // Обработчик изменения через инпут
  const handleNumberChange = (e) => {
    const inputValue = e.target.value;
    if (inputValue === "") {
      // Разрешаем пустое значение для редактирования
      return;
    }
    const newValue = parseInt(inputValue);
    if (isNaN(newValue)) return;
    if (minValue !== null && newValue < minValue) {
      onChange(minValue);
      return;
    }
    if (maxValue !== null && newValue > maxValue) {
      onChange(maxValue);
      return;
    }
    // Проверяем, что значение есть в опциях, или разрешаем любое значение в диапазоне
    if (options.length === 0 || options.includes(newValue)) {
      onChange(newValue);
    } else {
      // Если значение не в опциях, но в допустимом диапазоне, все равно применяем
      if (
        (minValue === null || newValue >= minValue) &&
        (maxValue === null || newValue <= maxValue)
      ) {
        onChange(newValue);
      }
    }
  };

  // Обработчик изменения через выпадающий список
  const handleSelectChange = (selectedOption, e) => {
    if (e) {
      e.stopPropagation();
    }
    isClickInsideRef.current = true;
    if (selectedOption) {
      onChange(selectedOption.value);
    }
    setIsOpen(false);
  };

  // Обработчик клика на инпут
  const handleInputClick = (e) => {
    e.stopPropagation();
    isClickInsideRef.current = true;
    setIsOpen((prev) => !prev);
  };

  // Закрываем выпадающий список при клике вне компонента
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      // Используем setTimeout чтобы дать время обработать клик внутри компонента
      setTimeout(() => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target) &&
          !isClickInsideRef.current
        ) {
          setIsOpen(false);
        }
        isClickInsideRef.current = false;
      }, 0);
    };

    if (isOpen) {
      // Используем click вместо mousedown для более надежной работы
      document.addEventListener("click", handleClickOutside, true);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, [isOpen]);

  return (
    <div
      ref={containerRef}
      style={{ position: "relative", display: "inline-block", width: "auto" }}
    >
      {/* Контейнер для инпута и стрелок */}
      <div style={{ position: "relative", display: "inline-block" }}>
        {/* Number input */}
        <input
          ref={inputRef}
          type="number"
          value={value || ""}
          onChange={handleNumberChange}
          onClick={handleInputClick}
          min={minValue !== null ? minValue : undefined}
          max={maxValue !== null ? maxValue : undefined}
          disabled={!effectiveIsEditable}
          style={{
            width: "60px",
            padding: "6px 24px 6px 8px",
            border: "1.5px solid #B8C2D1",
            borderRadius: "6px",
            fontSize: "15px",
            fontFamily: "Manrope, sans-serif",
            cursor: isEditable ? "pointer" : "not-allowed",
            backgroundColor: !isEditable ? "#F4F6FA" : "#FFFFFF",
            appearance: "textfield",
            WebkitAppearance: "textfield",
            MozAppearance: "textfield",
          }}
          onFocus={(e) => {
            // Выделяем текст при фокусе для удобства редактирования
            const input = e.target;
            requestAnimationFrame(() => input.select());
          }}
        />
        {/* Кастомные стрелки */}
        <div
          style={{
            position: "absolute",
            right: "4px",
            top: "50%",
            transform: "translateY(-50%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            pointerEvents: "none",
            zIndex: 1,
          }}
        >
          {/* Стрелка вверх */}
          <button
            type="button"
            onClick={handleIncrement}
            disabled={!effectiveIsEditable}
            onMouseDown={(e) => e.preventDefault()}
            className="number-select-arrow-btn"
            style={{
              background: "transparent",
              border: "none",
              padding: "2px",
              margin: "0",
              cursor: isEditable ? "pointer" : "not-allowed",
              pointerEvents: "auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "18px",
              height: "14px",
              opacity: isEditable ? 1 : 0.4,
              transition: "opacity 0.2s",
            }}
            onMouseEnter={(e) => {
              if (isEditable) {
                e.currentTarget.style.opacity = "1";
                e.currentTarget.style.backgroundColor = "#F4F6FA";
              }
            }}
            onMouseLeave={(e) => {
              if (isEditable) {
                e.currentTarget.style.opacity = "1";
                e.currentTarget.style.backgroundColor = "transparent";
              }
            }}
            title="Увеличить"
          >
            <svg
              width="8"
              height="5"
              viewBox="0 0 8 5"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4 0L0 4H8L4 0Z"
                fill={isEditable ? "#1A2343" : "#B8C2D1"}
              />
            </svg>
          </button>
          {/* Стрелка вниз */}
          <button
            type="button"
            onClick={handleDecrement}
            disabled={!effectiveIsEditable}
            onMouseDown={(e) => e.preventDefault()}
            className="number-select-arrow-btn"
            style={{
              background: "transparent",
              border: "none",
              padding: "2px",
              margin: "0",
              cursor: isEditable ? "pointer" : "not-allowed",
              pointerEvents: "auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "18px",
              height: "14px",
              opacity: isEditable ? 1 : 0.4,
              transition: "opacity 0.2s",
            }}
            onMouseEnter={(e) => {
              if (isEditable) {
                e.currentTarget.style.opacity = "1";
                e.currentTarget.style.backgroundColor = "#F4F6FA";
              }
            }}
            onMouseLeave={(e) => {
              if (isEditable) {
                e.currentTarget.style.opacity = "1";
                e.currentTarget.style.backgroundColor = "transparent";
              }
            }}
            title="Уменьшить"
          >
            <svg
              width="8"
              height="5"
              viewBox="0 0 8 5"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4 5L0 1H8L4 5Z"
                fill={isEditable ? "#1A2343" : "#B8C2D1"}
              />
            </svg>
          </button>
        </div>
      </div>
      {/* Выпадающий список */}
      {isOpen && isEditable && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            zIndex: 1000,
            backgroundColor: "#FFFFFF",
            border: "1px solid #B8C2D1",
            borderRadius: "6px",
            marginTop: "4px",
            maxHeight: "200px",
            overflowY: "auto",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            minWidth: "60px",
          }}
        >
          {selectOptions.map((option) => (
            <div
              key={option.value}
              onClick={(e) => handleSelectChange(option, e)}
              onMouseDown={(e) => {
                e.preventDefault();
                isClickInsideRef.current = true;
              }}
              style={{
                padding: "8px 12px",
                cursor: "pointer",
                backgroundColor:
                  option.value === value ? "#E6F3FF" : "transparent",
                color: option.value === value ? "#007bff" : "#1A2343",
                fontSize: "15px",
                fontFamily: "Manrope, sans-serif",
              }}
              onMouseEnter={(e) => {
                if (option.value !== value) {
                  e.target.style.backgroundColor = "#F4F6FA";
                }
              }}
              onMouseLeave={(e) => {
                if (option.value !== value) {
                  e.target.style.backgroundColor = "transparent";
                }
              }}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const BlockDetailForm = ({
  blockId,
  blockName,
  onDeleteClick,
  total,
  onCalcClick,
  onAddTypicalClick,
  typicalVisible,
  infoTypes,
  engrooms,
  blockData,
  onFormDataChange,
  onAutoCalc, // Новая функция для автоматической калькуляции
  currentFormData, // Передаем текущие данные формы
  isEditable = true, // Флаг редактируемости
  isParking = false, // Флаг, указывающий, является ли блок паркингом
}) => {
  const effectiveIsEditable = isEditable; // алиас для единообразия (родитель передаёт уже учтённый projectLockedByApproval)
  // Инициализируем состояние инженерных помещений на основе данных с сервера
  const [engineeringSystems, setEngineeringSystems] = useState(() => {
    if (blockData?.eng_rooms && blockData.eng_rooms.length > 0) {
      const initialEngSystems = {};
      blockData.eng_rooms.forEach((room) => {
        initialEngSystems[room.id] = true;
      });
      return initialEngSystems;
    }
    return {};
  });

  const [floorCount, setFloorCount] = useState(
    blockData?.floor_count && blockData.floor_count > 0
      ? blockData.floor_count
      : 1
  );

  // Обертка для setFloorCount с вызовом onAutoCalc
  const handleFloorCountChange = (value) => {
    // Валидация: этажность не может быть меньше 1 или пустой
    const numValue = parseInt(value);
    const validatedValue = isNaN(numValue) || numValue < 1 ? 1 : numValue;
    setFloorCount(validatedValue);
    // Обновляем formData
    onFormDataChange(blockId, { floor_count: validatedValue });
    // Вызываем автоматическую калькуляцию после изменения этажности
    if (onAutoCalc) {
      setTimeout(() => onAutoCalc({ floor_count: validatedValue }), 200);
    }
  };

  const [infoType, setInfoType] = useState(() => {
    // Если у блока уже есть тип инфраструктуры, используем его
    if (blockData?.inf_type?.id) {
      return blockData.inf_type.id.toString();
    }
    // Если это новый блок и есть доступные типы, выбираем первый
    if (infoTypes && infoTypes.length > 0) {
      return infoTypes[0].id.toString();
    }
    return "";
  });
  const [basementArea, setBasementArea] = useState(
    blockData?.basement_area?.toString() || "0"
  );

  const [apartmentsCount, setApartmentsCount] = useState("0");
  const [nonTypicalFloors, setNonTypicalFloors] = useState([]); // нетипичные этажи из API
  const [parkingArea, setParkingArea] = useState(
    blockData?.total_area?.toString() || "0"
  );

  // Используем useRef для отслеживания предыдущего blockId, чтобы избежать циклов при переключении блоков
  const prevBlockIdRef = React.useRef(blockId);
  const prevBlockDataRef = React.useRef(null);
  const prevIsParkingRef = React.useRef(isParking);

  // Редактируемый инпут в стиле WorkTab.js (без логики пересчётов)
  const InlineEditable = ({
    value,
    onSave,
    width = 120,
    inputProps = {},
    isEditableLocal = isEditable,
  }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [localValue, setLocalValue] = useState(value);
    const inputRef = React.useRef(null);
    const computeWidthPx = (val) => {
      const text =
        val === undefined || val === null || val === "" ? "0" : String(val);
      const charCount = text.length;
      return Math.max(30, charCount * 10 + 16);
    };

    useEffect(() => {
      setLocalValue(value);
    }, [value]);

    useEffect(() => {
      if (isEditing && inputRef.current) {
        inputRef.current.focus();
        const input = inputRef.current;
        requestAnimationFrame(() => {
          try {
            input.select();
          } catch {}
        });
      }
    }, [isEditing]);

    if (isEditing) {
      const wContent = computeWidthPx(localValue);
      const w = Math.max(width, wContent);
      return (
        <div
          style={{
            position: "relative",
            width: `${w}px`,
            margin: 0,
            display: "inline-block",
          }}
        >
          <input
            {...{
              type: "number",
              step: "0.01",
              min: "0",
              inputMode: "decimal",
              ...inputProps,
            }}
            value={localValue}
            ref={inputRef}
            onChange={(e) => setLocalValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                // Убираем ведущие нули из числовых значений
                const cleanValue =
                  localValue === "" ? "0" : String(Number(localValue));
                onSave(cleanValue);
                setIsEditing(false);
              } else if (e.key === "Escape") {
                setLocalValue(value);
                setIsEditing(false);
              }
            }}
            style={{
              width: `${w}px`,
              padding: "6px 10px",
              border: "1.5px dashed #B8C2D1",
              borderRadius: "6px",
              background: "#FFFFFF",
              fontSize: "15px",
              fontFamily: "Manrope, sans-serif",
              textAlign: "left",
              outline: "none",
              transition: "border-color 0.2s",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#007bff")}
            onBlur={(e) => {
              e.target.style.borderColor = "#ccc";
              // Автоматически сохраняем при потере фокуса
              const cleanValue =
                localValue === "" ? "0" : String(Number(localValue));
              onSave(cleanValue);
              setIsEditing(false);
            }}
            disabled={!isEditableLocal}
          />
        </div>
      );
    }

    const displayValue = value ?? "0";
    const wViewContent = computeWidthPx(displayValue);
    const wView = Math.max(width, wViewContent);
    return (
      <div
        style={{
          width: `${wView}px`,
          margin: 0,
          textAlign: "left",
          display: "inline-block",
        }}
      >
        <span
          style={{
            fontSize: "15px",
            fontFamily: "Manrope, sans-serif",
            display: "inline-block",
            width: `${wView}px`,
            padding: "6px 10px",
            border: "1.5px dashed #B8C2D1",
            borderRadius: "6px",
            background: !isEditableLocal ? "#F4F6FA" : "#F9FBFF",
            whiteSpace: "nowrap",
            cursor: !isEditableLocal ? "default" : "pointer",
            textAlign: "left",
          }}
          onClick={() => (!isEditableLocal ? undefined : setIsEditing(true))}
        >
          {displayValue}
        </span>
      </div>
    );
  };

  // Ref для отслеживания предыдущих данных формы
  const prevFormData = React.useRef();
  const prevParkingAreaRef = React.useRef(
    blockData?.total_area?.toString() || "0"
  );

  // Флаг для отслеживания первой инициализации
  const isFirstMountRef = React.useRef(true);

  // Синхронизируем состояние с currentFormData при переключении блоков
  useEffect(() => {
    const blockIdChanged = prevBlockIdRef.current !== blockId;

    // Если есть сохраненные данные формы, применяем их
    if (currentFormData && Object.keys(currentFormData).length > 0) {
      // Применяем сохраненные данные формы при переключении блоков или первом монтировании
      if (blockIdChanged || isFirstMountRef.current) {
        if (currentFormData.floor_count !== undefined) {
          setFloorCount(
            currentFormData.floor_count > 0 ? currentFormData.floor_count : 1
          );
        }
        if (currentFormData.basement_area !== undefined) {
          setBasementArea(currentFormData.basement_area.toString());
        }
        if (currentFormData.apartments_count !== undefined) {
          setApartmentsCount(currentFormData.apartments_count.toString());
        }
        if (currentFormData.inf_type_id !== undefined) {
          setInfoType(currentFormData.inf_type_id.toString());
        }
        if (currentFormData.total_area !== undefined && isParking) {
          setParkingArea(currentFormData.total_area.toString());
        }
        if (
          currentFormData.eng_room_ids !== undefined &&
          Array.isArray(currentFormData.eng_room_ids)
        ) {
          const engSystems = {};
          currentFormData.eng_room_ids.forEach((id) => {
            engSystems[id] = true;
          });
          setEngineeringSystems(engSystems);
        }
        // ВАЖНО: При переключении блоков всегда используем данные из formData для текущего блока
        // Если данных нет в formData для этого блока, очищаем этажи
        if (
          currentFormData.non_typical_floors !== undefined &&
          Array.isArray(currentFormData.non_typical_floors)
        ) {
          if (currentFormData.non_typical_floors.length > 0) {
            // Если есть сохраненные этажи, используем их
            const floors = currentFormData.non_typical_floors.map((floor) => ({
              id: floor.id || Date.now() + Math.random(),
              floorNumber: floor.floorNumber || floor.floor_number || 0,
              totalArea: (floor.totalArea || floor.total_area || 0).toString(),
              commercialArea: (
                floor.commercialArea ||
                floor.commercial_area ||
                0
              ).toString(),
              otherArea: (floor.otherArea || floor.other_area || 0).toString(),
            }));
            setNonTypicalFloors(floors);
          } else {
            // Если в formData пустой массив, очищаем этажи
            // Для не-паркинга создадим первый этаж в следующем useEffect
            setNonTypicalFloors([]);
          }
        } else {
          // Если нет данных в formData для этого блока, очищаем этажи
          setNonTypicalFloors([]);
        }
      }
    } else {
      // Если нет сохраненных данных формы для этого блока, очищаем этажи при переключении
      if (blockIdChanged) {
        setNonTypicalFloors([]);
      }
    }
  }, [blockId, currentFormData, isParking]);

  // Обновляем состояние при изменении blockData (только если нет сохраненных данных формы)
  useEffect(() => {
    // Проверяем, действительно ли изменился блок или его данные
    const blockIdChanged = prevBlockIdRef.current !== blockId;
    const blockDataChanged =
      !prevBlockDataRef.current ||
      JSON.stringify(prevBlockDataRef.current) !== JSON.stringify(blockData);
    const isParkingChanged = prevIsParkingRef.current !== isParking;

    // Обновляем refs
    prevBlockIdRef.current = blockId;
    prevBlockDataRef.current = blockData ? { ...blockData } : null;
    prevIsParkingRef.current = isParking;

    // При первой загрузке или смене блока обновляем все
    if (isFirstMountRef.current || blockIdChanged) {
      isFirstMountRef.current = false;
      // Продолжаем выполнение для обновления состояния
    } else if (!blockDataChanged && !isParkingChanged) {
      // Обновляем только если блок действительно изменился
      return;
    }

    // Если есть сохраненные данные формы, не используем blockData для этих полей
    const hasFormData =
      currentFormData && Object.keys(currentFormData).length > 0;

    // Обновляем форму при изменении blockData (только для полей, которых нет в сохраненных данных)
    if (!hasFormData || !currentFormData.eng_room_ids) {
      if (blockData?.eng_rooms && blockData.eng_rooms.length > 0) {
        const initialEngSystems = {};
        blockData.eng_rooms.forEach((room) => {
          initialEngSystems[room.id] = true;
        });
        setEngineeringSystems(initialEngSystems);
      } else if (!hasFormData) {
        setEngineeringSystems({});
      }
    }

    if (!hasFormData || currentFormData.floor_count === undefined) {
      setFloorCount(
        blockData?.floor_count && blockData.floor_count > 0
          ? blockData.floor_count
          : 1
      );
    }

    // Обновляем тип инфраструктуры
    if (!hasFormData || currentFormData.inf_type_id === undefined) {
      if (blockData?.inf_type?.id) {
        setInfoType(blockData.inf_type.id.toString());
      } else if (infoTypes && infoTypes.length > 0) {
        // Если у блока нет типа инфраструктуры, выбираем первый доступный
        setInfoType(infoTypes[0].id.toString());
      } else {
        setInfoType("");
      }
    }

    if (!hasFormData || currentFormData.basement_area === undefined) {
      setBasementArea(blockData?.basement_area?.toString() || "0");
    }

    if (!hasFormData || currentFormData.apartments_count === undefined) {
      setApartmentsCount(blockData?.apartments_count?.toString() || "0");
    }

    if (
      !hasFormData ||
      (isParking && currentFormData.total_area === undefined)
    ) {
      const newParkingArea = blockData?.total_area?.toString() || "0";
      // Обновляем только если значение изменилось, чтобы избежать циклов
      if (newParkingArea !== prevParkingAreaRef.current) {
        setParkingArea(newParkingArea);
        prevParkingAreaRef.current = newParkingArea;
      }
    }

    // Обрабатываем нетипичные этажи из API (только если нет сохраненных данных)
    // ВАЖНО: Если есть сохраненные данные в formData, используем их, а не данные из blockData
    if (
      hasFormData &&
      currentFormData.non_typical_floors !== undefined &&
      Array.isArray(currentFormData.non_typical_floors)
    ) {
      // Если есть сохраненные данные в formData (даже пустой массив), используем их
      if (currentFormData.non_typical_floors.length > 0) {
        const floors = currentFormData.non_typical_floors.map((floor) => ({
          id: floor.id || Date.now() + Math.random(),
          floorNumber: floor.floorNumber || floor.floor_number || 0,
          totalArea: (floor.totalArea || floor.total_area || 0).toString(),
          commercialArea: (
            floor.commercialArea ||
            floor.commercial_area ||
            0
          ).toString(),
          otherArea: (floor.otherArea || floor.other_area || 0).toString(),
        }));
        // Проверяем, изменились ли данные перед обновлением
        const currentFloorsStr = JSON.stringify(nonTypicalFloors);
        const newFloorsStr = JSON.stringify(floors);
        if (currentFloorsStr !== newFloorsStr) {
          setNonTypicalFloors(floors);
        }
      } else {
        // Если в formData пустой массив, очищаем nonTypicalFloors
        // Но для не-паркинга создаем первый этаж, если его еще нет
        if (!isParking) {
          // Если это не паркинг и нет этажей, создаем первый этаж
          if (nonTypicalFloors.length === 0) {
            const firstFloor = {
              id: Date.now() + Math.random(),
              floorNumber: 1,
              totalArea: "0",
              commercialArea: "0",
              otherArea: "0",
            };
            setNonTypicalFloors([firstFloor]);
          } else {
            // Очищаем существующие этажи, если в formData пустой массив
            setNonTypicalFloors([]);
            // Создаем первый этаж после очистки
            setTimeout(() => {
              const firstFloor = {
                id: Date.now() + Math.random(),
                floorNumber: 1,
                totalArea: "0",
                commercialArea: "0",
                otherArea: "0",
              };
              setNonTypicalFloors([firstFloor]);
            }, 0);
          }
        } else {
          // Для паркинга просто очищаем
          setNonTypicalFloors([]);
        }
      }
    } else if (
      !hasFormData ||
      currentFormData.non_typical_floors === undefined
    ) {
      // Если нет сохраненных данных в formData для этого блока, используем данные из API или создаем пустой массив
      if (
        blockData?.non_typical_floors &&
        blockData.non_typical_floors.length > 0
      ) {
        const nonTypicalFloorsData = blockData.non_typical_floors.map(
          (floor) => ({
            id: floor.id,
            floorNumber: floor.floor_number,
            totalArea: floor.total_area?.toString() || "0",
            commercialArea: floor.commercial_area?.toString() || "0",
            otherArea: floor.other_area?.toString() || "0",
          })
        );
        // Проверяем, изменились ли нетипичные этажи перед обновлением
        const currentFloorsStr = JSON.stringify(nonTypicalFloors);
        const newFloorsStr = JSON.stringify(nonTypicalFloorsData);
        if (currentFloorsStr !== newFloorsStr) {
          setNonTypicalFloors(nonTypicalFloorsData);
        }
      } else {
        // Если это не паркинг и нет данных о нетипичных этажах, автоматически создаем первый этаж
        if (!isParking) {
          // Очищаем существующие этажи перед созданием нового
          setNonTypicalFloors([]);
          // Создаем первый этаж
          const firstFloor = {
            id: Date.now() + Math.random(),
            floorNumber: 1,
            totalArea: "0",
            commercialArea: "0",
            otherArea: "0",
          };
          setNonTypicalFloors([firstFloor]);
        } else if (isParking && nonTypicalFloors.length > 0) {
          // Очищаем нетипичные этажи только если действительно переключились на паркинг
          setNonTypicalFloors([]);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    blockId,
    blockData?.id,
    blockData?.non_typical_floors?.length,
    isParking,
  ]); // Добавляем isParking в зависимости

  // Используем useRef для отслеживания предыдущих значений массивов
  const prevNonTypicalFloorsRef = React.useRef(
    JSON.stringify(nonTypicalFloors)
  );
  const prevEngSystemsRef = React.useRef(JSON.stringify(engineeringSystems));
  const prevIsParkingForFormRef = React.useRef(isParking);
  const prevBlockIdForFormRef = React.useRef(blockId);

  // Вычисляем стабильные ключи для отслеживания изменений массивов
  const nonTypicalFloorsKey = React.useMemo(
    () => JSON.stringify(nonTypicalFloors),
    [nonTypicalFloors]
  );
  const engSystemsKey = React.useMemo(
    () =>
      Object.keys(engineeringSystems)
        .filter((id) => engineeringSystems[id])
        .sort()
        .join(","),
    [engineeringSystems]
  );

  // Флаг для отслеживания первой инициализации блока
  const isInitialMountRef = React.useRef(true);
  const skipNextUpdateRef = React.useRef(false);

  // Отправляем данные формы обратно в родительский компонент
  useEffect(() => {
    if (!onFormDataChange) return;

    // Пропускаем первый рендер при монтировании или смене блока
    if (isInitialMountRef.current || skipNextUpdateRef.current) {
      isInitialMountRef.current = false;
      skipNextUpdateRef.current = false;

      // Обновляем refs при первой инициализации
      const nonTypicalFloorsStr = JSON.stringify(nonTypicalFloors);
      const engSystemsStr = JSON.stringify(engineeringSystems);
      prevBlockIdForFormRef.current = blockId;
      prevNonTypicalFloorsRef.current = nonTypicalFloorsStr;
      prevEngSystemsRef.current = engSystemsStr;
      prevIsParkingForFormRef.current = isParking;
      prevFormData.current = null;
      return;
    }

    // Вычисляем строковые представления массивов
    const nonTypicalFloorsStr = JSON.stringify(nonTypicalFloors);
    const engSystemsStr = JSON.stringify(engineeringSystems);

    // Проверяем, изменились ли массивы или блок
    const nonTypicalFloorsChanged =
      prevNonTypicalFloorsRef.current !== nonTypicalFloorsStr;
    const engSystemsChanged = prevEngSystemsRef.current !== engSystemsStr;
    const isParkingChanged = prevIsParkingForFormRef.current !== isParking;
    const blockIdChanged = prevBlockIdForFormRef.current !== blockId;

    // Если изменился blockId, ВАЖНО: сначала сохраняем данные текущего блока перед переключением
    if (blockIdChanged) {
      // Сохраняем данные предыдущего блока перед переключением
      const prevBlockId = prevBlockIdForFormRef.current;
      if (
        prevBlockId &&
        (nonTypicalFloorsChanged || engSystemsChanged || isParkingChanged)
      ) {
        const prevBlockFormData = {
          ...(prevIsParkingForFormRef.current
            ? {
                total_area: parseFloat(parkingArea) || 0,
              }
            : {
                floor_count: floorCount,
                basement_area: parseFloat(basementArea) || 0,
                apartments_count: parseInt(apartmentsCount) || 0,
                non_typical_floors: nonTypicalFloors,
              }),
          inf_type_id: parseInt(infoType) || 0,
          eng_room_ids: Object.keys(engineeringSystems)
            .filter((id) => engineeringSystems[id])
            .map((id) => parseInt(id)),
        };
        onFormDataChange(prevBlockId, prevBlockFormData);
      }

      // Теперь обновляем refs для нового блока
      prevBlockIdForFormRef.current = blockId;
      prevNonTypicalFloorsRef.current = nonTypicalFloorsStr;
      prevEngSystemsRef.current = engSystemsStr;
      prevIsParkingForFormRef.current = isParking;
      prevFormData.current = null;
      isInitialMountRef.current = true; // Сбрасываем флаг для нового блока
      skipNextUpdateRef.current = true;
      return;
    }

    // Обновляем refs только если значения изменились
    if (nonTypicalFloorsChanged) {
      prevNonTypicalFloorsRef.current = nonTypicalFloorsStr;
    }
    if (engSystemsChanged) {
      prevEngSystemsRef.current = engSystemsStr;
    }
    if (isParkingChanged) {
      prevIsParkingForFormRef.current = isParking;
    }

    const currentFormData = {
      floor_count: floorCount,
      ...(isParking
        ? {
            total_area: parseFloat(parkingArea) || 0,
          }
        : {
            basement_area: parseFloat(basementArea) || 0,
            apartments_count: parseInt(apartmentsCount) || 0,
            non_typical_floors: nonTypicalFloors,
          }),
      inf_type_id: parseInt(infoType) || 0,
      eng_room_ids: Object.keys(engineeringSystems)
        .filter((id) => engineeringSystems[id])
        .map((id) => parseInt(id)),
    };

    // Проверяем, изменились ли данные, чтобы избежать лишних вызовов
    const prevDataStr = prevFormData.current
      ? JSON.stringify(prevFormData.current)
      : null;
    const currentDataStr = JSON.stringify(currentFormData);

    // Вызываем onFormDataChange только если данные действительно изменились
    if (prevDataStr !== currentDataStr) {
      prevFormData.current = currentFormData;
      // Используем requestAnimationFrame для асинхронного вызова
      requestAnimationFrame(() => {
        onFormDataChange(blockId, currentFormData);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    floorCount,
    basementArea,
    infoType,
    apartmentsCount,
    blockId,
    isParking,
    parkingArea,
    nonTypicalFloorsKey,
    engSystemsKey,
  ]);

  return (
    <form
      className="details-form-grid form-maket-layout"
      onSubmit={(e) => e.preventDefault()}
    >
      <div className="form-group form-group-full compact-group">
        <label>Выберите этажность*</label>
        <NumberSelect
          value={floorCount}
          onChange={handleFloorCountChange}
          options={Array.from({ length: 50 }, (_, i) => i + 1)}
          minValue={1}
          isEditable={effectiveIsEditable}
          placeholder="Выберите этажность"
        />
      </div>

      <div className="form-group form-group-full">
        <label>Определите тип секции</label>
        <div className="radio-group">
          {infoTypes && infoTypes.length > 0 ? (
            infoTypes.map((type) => (
              <label key={type.id}>
                <input
                  type="radio"
                  name={`infoType-${blockId}`}
                  value={type.id.toString()}
                  checked={infoType === type.id.toString()}
                  onChange={(e) => {
                    setInfoType(e.target.value);
                    // Обновляем formData
                    onFormDataChange(blockId, {
                      inf_type_id: parseInt(e.target.value) || 0,
                    });
                    // Вызываем автоматическую калькуляцию после изменения типа информации
                    if (onAutoCalc) {
                      setTimeout(
                        () =>
                          onAutoCalc({
                            inf_type_id: parseInt(e.target.value) || 0,
                          }),
                        200
                      );
                    }
                  }}
                />
                {type.name}
              </label>
            ))
          ) : (
            <div>Загрузка типов информации...</div>
          )}
        </div>
      </div>

      <div className="form-group form-group-full">
        <label>Выберите наличие имеющиеся инженерных помещений</label>
        <div className="checkbox-group">
          {engrooms && engrooms.length > 0 ? (
            engrooms.map((room) => (
              <label key={room.id}>
                <input
                  type="checkbox"
                  checked={engineeringSystems[room.id] || false}
                  onChange={(e) =>
                    setEngineeringSystems({
                      ...engineeringSystems,
                      [room.id]: e.target.checked,
                    })
                  }
                  disabled={!effectiveIsEditable}
                />
                {room.name}
              </label>
            ))
          ) : (
            <div>Загрузка типов инженерных помещений...</div>
          )}
        </div>
      </div>

      {!isParking && (
        <div className="form-group form-group-full compact-group">
          <label>Введите количество квартир на площадке</label>
          <InlineEditable
            value={apartmentsCount}
            isEditable={effectiveIsEditable}
            onSave={(v) => {
              setApartmentsCount(v);
              // Обновляем formData
              onFormDataChange(blockId, { apartments_count: parseInt(v) || 0 });
              // Вызываем автоматическую калькуляцию после изменения количества квартир
              if (onAutoCalc) {
                setTimeout(
                  () => onAutoCalc({ apartments_count: parseInt(v) || 0 }),
                  200
                );
              }
            }}
            width={80}
            inputProps={{
              type: "number",
              step: "1",
              min: "0",
              inputMode: "numeric",
            }}
          />
        </div>
      )}

      {!isParking && (
        <div className="form-group section-title-row">
          {/* <div className="section-caption">Подвал</div> */}
          <label>Введите площадь подвала (м2)*</label>
          <InlineEditableWithMath
            value={basementArea}
            isEditable={effectiveIsEditable}
            onSave={(v) => {
              setBasementArea(v);
              // Обновляем formData
              onFormDataChange(blockId, { basement_area: parseFloat(v) || 0 });
              // Вызываем автоматическую калькуляцию после изменения площади подвала
              if (onAutoCalc) {
                setTimeout(
                  () => onAutoCalc({ basement_area: parseFloat(v) || 0 }),
                  200
                );
              }
            }}
            width={80}
          />
        </div>
      )}

      {/* Поле для ввода площади паркинга */}
      {isParking && (
        <div className="form-group form-group-full compact-group">
          <label>Введите площадь паркинга (м2)*</label>
          <InlineEditableWithMath
            value={parkingArea}
            isEditable={effectiveIsEditable}
            onSave={(v) => {
              // Только обновляем состояние, useEffect сам вызовет onFormDataChange
              setParkingArea(v);
            }}
            width={120}
            inputProps={{
              type: "number",
              step: "0.01",
              min: "0",
              inputMode: "decimal",
            }}
          />
        </div>
      )}

      {/* Нетипичные этажи из API: 1-й этаж — с разбивкой (коммерческая + остальные), остальные — только общая площадь */}
      {!isParking && (
        <>
          {/* 1-й этаж: общая площадь + коммерческая + остальные */}
          {nonTypicalFloors
            .filter((f) => f.floorNumber === 1)
            .map((f) => (
              <div
                className="form-group form-group-full two-col-row floor-group"
                key={f.id}
                style={{ position: "relative" }}
              >
                <button
                  type="button"
                  className="close-typical-form-btn"
                  onClick={() => {
                    const nextFloors = nonTypicalFloors
                      .filter((x) => x.id !== f.id)
                      .map((x, i) => ({
                        ...x,
                        floorNumber: i + 1,
                      }));
                    setNonTypicalFloors(nextFloors);
                    if (onAutoCalc && nextFloors.length > 0) {
                      setTimeout(() => {
                        onAutoCalc({
                          non_typical_floors: nextFloors.map((floor) => ({
                            commercial_area: parseNumericValue(
                              floor.commercialArea || "0"
                            ),
                            other_area: parseNumericValue(
                              floor.otherArea || "0"
                            ),
                            floor_number: floor.floorNumber,
                            total_area:
                              parseNumericValue(floor.totalArea || "0") ||
                              parseNumericValue(floor.commercialArea || "0") +
                                parseNumericValue(floor.otherArea || "0"),
                          })),
                        });
                      }, 100);
                    }
                  }}
                  disabled={!effectiveIsEditable || nonTypicalFloors.length <= 1}
                  style={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    background: "none",
                    border: "none",
                    fontSize: 22,
                    cursor:
                      !isEditable || nonTypicalFloors.length <= 1
                        ? "not-allowed"
                        : "pointer",
                    color:
                      !isEditable || nonTypicalFloors.length <= 1
                        ? "#ccc"
                        : "#7B8AAB",
                    zIndex: 2,
                    opacity:
                      !isEditable || nonTypicalFloors.length <= 1 ? 0.5 : 1,
                  }}
                  aria-label="Удалить этаж"
                >
                  ×
                </button>
                <div className="left-col">
                  <label>1-й этаж (м2)</label>
                  <InlineEditableWithMath
                    value={f.totalArea}
                    isEditable={effectiveIsEditable}
                    onSave={(v) => {
                      const total = parseNumericValue(v);
                      setNonTypicalFloors((prev) => {
                        const newState = prev.map((x) => {
                          if (x.id === f.id) {
                            const commercialPrev =
                              parseFloat(f.commercialArea) || 0;
                            const otherPrev = parseFloat(f.otherArea) || 0;
                            let newCommercial = commercialPrev;
                            let newOther = otherPrev;
                            if (total <= 0) {
                              newCommercial = 0;
                              newOther = 0;
                            } else if (
                              commercialPrev === 0 &&
                              otherPrev === 0
                            ) {
                              newCommercial = 0;
                              newOther = total;
                            } else if (commercialPrev >= total) {
                              newCommercial = total;
                              newOther = 0;
                            } else {
                              newCommercial = commercialPrev;
                              newOther = smartRound(
                                Math.max(0, total - newCommercial)
                              );
                            }
                            return {
                              ...x,
                              totalArea: v,
                              commercialArea: newCommercial.toString(),
                              otherArea: newOther.toString(),
                            };
                          }
                          return x;
                        });
                        if (onAutoCalc) {
                          setTimeout(() => {
                            onAutoCalc({
                              non_typical_floors: newState.map((floor) => {
                                const commercialArea = parseNumericValue(
                                  floor.commercialArea || "0"
                                );
                                const otherArea = parseNumericValue(
                                  floor.otherArea || "0"
                                );
                                const floorNumber =
                                  parseInt(floor.floorNumber) || 0;
                                const totalAreaValue = parseNumericValue(
                                  floor.totalArea || "0"
                                );
                                const calculatedTotalArea =
                                  totalAreaValue > 0
                                    ? totalAreaValue
                                    : commercialArea + otherArea;
                                return {
                                  commercial_area: commercialArea,
                                  other_area: otherArea,
                                  floor_number: floorNumber,
                                  total_area: calculatedTotalArea,
                                };
                              }),
                            });
                          }, 500);
                        }
                        return newState;
                      });
                    }}
                    width={120}
                    inputProps={{
                      type: "number",
                      step: "0.01",
                      min: "0",
                      inputMode: "decimal",
                    }}
                  />
                </div>
                <div
                  className="right-col"
                  style={{ display: "flex", gap: "24px" }}
                >
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Коммерческие (м2)</label>
                    <InlineEditableWithMath
                      value={f.commercialArea}
                      isEditable={effectiveIsEditable}
                      onSave={(v) => {
                        const total = parseNumericValue(f.totalArea || "0");
                        const commercial = parseNumericValue(v);
                        const other = parseNumericValue(f.otherArea || "0");
                        setNonTypicalFloors((prev) => {
                          const newState = prev.map((x) =>
                            x.id === f.id
                              ? {
                                  ...x,
                                  commercialArea: v,
                                  otherArea:
                                    commercial <= total && total > 0
                                      ? smartRound(
                                          total - commercial
                                        ).toString()
                                      : x.otherArea,
                                  totalArea:
                                    total <= 0
                                      ? smartRound(commercial + other).toString()
                                      : x.totalArea,
                                }
                              : x
                          );
                          if (onAutoCalc) {
                            setTimeout(() => {
                              setNonTypicalFloors((currentState) => {
                                onAutoCalc({
                                  non_typical_floors: currentState.map(
                                    (floor) => {
                                      const commercialArea = parseNumericValue(
                                        floor.commercialArea || "0"
                                      );
                                      const otherArea = parseNumericValue(
                                        floor.otherArea || "0"
                                      );
                                      const floorNumber =
                                        parseInt(floor.floorNumber) || 0;
                                      const totalAreaValue = parseNumericValue(
                                        floor.totalArea || "0"
                                      );
                                      const calculatedTotalArea =
                                        totalAreaValue > 0
                                          ? totalAreaValue
                                          : commercialArea + otherArea;
                                      return {
                                        commercial_area: commercialArea,
                                        other_area: otherArea,
                                        floor_number: floorNumber,
                                        total_area: calculatedTotalArea,
                                      };
                                    }
                                  ),
                                });
                                return currentState;
                              });
                            }, 500);
                          }
                          return newState;
                        });
                      }}
                      width={120}
                    />
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Остальные (м2)</label>
                    <InlineEditableWithMath
                      value={f.otherArea}
                      isEditable={effectiveIsEditable}
                      onSave={(v) => {
                        const total = parseNumericValue(f.totalArea || "0");
                        const other = parseNumericValue(v);
                        const commercial = parseNumericValue(
                          f.commercialArea || "0"
                        );
                        setNonTypicalFloors((prev) => {
                          const newState = prev.map((x) =>
                            x.id === f.id
                              ? {
                                  ...x,
                                  otherArea: v,
                                  commercialArea:
                                    other <= total && total > 0
                                      ? smartRound(total - other).toString()
                                      : x.commercialArea,
                                  totalArea:
                                    total <= 0
                                      ? smartRound(
                                          commercial + other
                                        ).toString()
                                      : x.totalArea,
                                }
                              : x
                          );
                          if (onAutoCalc) {
                            setTimeout(() => {
                              setNonTypicalFloors((currentState) => {
                                onAutoCalc({
                                  non_typical_floors: currentState.map(
                                    (floor) => {
                                      const commercialArea = parseNumericValue(
                                        floor.commercialArea || "0"
                                      );
                                      const otherArea = parseNumericValue(
                                        floor.otherArea || "0"
                                      );
                                      const floorNumber =
                                        parseInt(floor.floorNumber) || 0;
                                      const totalAreaValue = parseNumericValue(
                                        floor.totalArea || "0"
                                      );
                                      const calculatedTotalArea =
                                        totalAreaValue > 0
                                          ? totalAreaValue
                                          : commercialArea + otherArea;
                                      return {
                                        commercial_area: commercialArea,
                                        other_area: otherArea,
                                        floor_number: floorNumber,
                                        total_area: calculatedTotalArea,
                                      };
                                    }
                                  ),
                                });
                                return currentState;
                              });
                            }, 500);
                          }
                          return newState;
                        });
                      }}
                      width={120}
                    />
                  </div>
                </div>
              </div>
            ))}

          {/* Этажи 2 и далее: только общая площадь + крестик удаления */}
          {nonTypicalFloors
            .filter((f) => f.floorNumber !== 1)
            .sort((a, b) => (a.floorNumber || 0) - (b.floorNumber || 0))
            .map((f) => (
              <div
                className="form-group form-group-full floor-group"
                key={f.id}
                style={{ position: "relative" }}
              >
                <button
                  type="button"
                  className="close-typical-form-btn"
                  onClick={() => {
                    const nextFloors = nonTypicalFloors
                      .filter((x) => x.id !== f.id)
                      .map((x, i) => ({
                        ...x,
                        floorNumber: i + 1,
                      }));
                    setNonTypicalFloors(nextFloors);
                    if (onAutoCalc && nextFloors.length > 0) {
                      setTimeout(() => {
                        onAutoCalc({
                          non_typical_floors: nextFloors.map((floor) => ({
                            commercial_area: parseNumericValue(
                              floor.commercialArea || "0"
                            ),
                            other_area: parseNumericValue(
                              floor.otherArea || "0"
                            ),
                            floor_number: floor.floorNumber,
                            total_area:
                              parseNumericValue(floor.totalArea || "0") ||
                              parseNumericValue(floor.commercialArea || "0") +
                                parseNumericValue(floor.otherArea || "0"),
                          })),
                        });
                      }, 100);
                    }
                  }}
                  disabled={!effectiveIsEditable}
                  style={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    background: "none",
                    border: "none",
                    fontSize: 22,
                    cursor: !isEditable ? "not-allowed" : "pointer",
                    color: !isEditable ? "#ccc" : "#7B8AAB",
                    zIndex: 2,
                    opacity: !isEditable ? 0.5 : 1,
                  }}
                  aria-label="Удалить этаж"
                >
                  ×
                </button>
                <label>{f.floorNumber}-й этаж (м2)</label>
                <InlineEditableWithMath
                  value={f.totalArea}
                  isEditable={effectiveIsEditable}
                  onSave={(v) => {
                    setNonTypicalFloors((prev) => {
                      const newState = prev.map((x) =>
                        x.id === f.id
                          ? {
                              ...x,
                              totalArea: v,
                              commercialArea: "0",
                              otherArea: v,
                            }
                          : x
                      );
                      if (onAutoCalc) {
                        setTimeout(() => {
                          onAutoCalc({
                            non_typical_floors: newState.map((floor) => {
                              const commercialArea = parseNumericValue(
                                floor.commercialArea || "0"
                              );
                              const otherArea = parseNumericValue(
                                floor.otherArea || "0"
                              );
                              const floorNumber =
                                parseInt(floor.floorNumber) || 0;
                              const totalAreaValue = parseNumericValue(
                                floor.totalArea || "0"
                              );
                              const calculatedTotalArea =
                                totalAreaValue > 0
                                  ? totalAreaValue
                                  : commercialArea + otherArea;
                              return {
                                commercial_area: commercialArea,
                                other_area: otherArea,
                                floor_number: floorNumber,
                                total_area: calculatedTotalArea,
                              };
                            }),
                          });
                        }, 500);
                      }
                      return newState;
                    });
                  }}
                  width={120}
                  inputProps={{
                    type: "number",
                    step: "0.01",
                    min: "0",
                    inputMode: "decimal",
                  }}
                />
              </div>
            ))}

          {/* Кнопка для добавления нетипичных этажей */}
          <div className="form-group form-group-full">
            <button
              type="button"
              className="btn-add-floor"
              onClick={() => {
                const newFloor = {
                  id: Date.now() + Math.random(),
                  floorNumber: nonTypicalFloors.length + 1,
                  totalArea: "0",
                  commercialArea: "0",
                  otherArea: "0",
                };
                setNonTypicalFloors((prev) => {
                  const newState = [...prev, newFloor];
                  return newState;
                });

                // Вызываем автоматическую калькуляцию после добавления нетипичного этажа
                setTimeout(() => {
                  if (onAutoCalc) {
                    // Передаем актуальные данные нетипичных этажей включая новый этаж
                    // Передаем totalArea для всех этажей, если оно введено пользователем
                    const updatedFloors = [...nonTypicalFloors, newFloor];
                    onAutoCalc({
                      non_typical_floors: updatedFloors.map((floor) => {
                        const commercialArea = parseNumericValue(
                          floor.commercialArea || "0"
                        );
                        const otherArea = parseNumericValue(
                          floor.otherArea || "0"
                        );
                        const floorNumber = parseInt(floor.floorNumber) || 0;
                        const totalAreaValue = parseNumericValue(
                          floor.totalArea || "0"
                        );
                        // Вычисляем total_area как сумму коммерческой и остальной площади,
                        // если totalArea не введена или равна 0
                        const calculatedTotalArea =
                          totalAreaValue > 0
                            ? totalAreaValue
                            : commercialArea + otherArea;
                        return {
                          commercial_area: commercialArea,
                          other_area: otherArea,
                          floor_number: floorNumber,
                          total_area: calculatedTotalArea,
                        };
                      }),
                    });
                  }
                }, 100);
              }}
              disabled={!effectiveIsEditable}
            >
              + Добавить этаж
            </button>
          </div>
        </>
      )}
    </form>
  );
};

const TypicalFloorsForm = ({
  typicalStart,
  setTypicalStart,
  typicalEnd,
  setTypicalEnd,
  typicalArea,
  setTypicalArea,
  oneFloorArea,
  setOneFloorArea,
  typicalCount,
  setTypicalCount,
  onCalcClick,
  onTypicalCalcClick,
  onClose,
  form,
  isEditable = true,
  maxFloorCount = null, // Максимальное значение этажности из левой части
  minTypicalFloor = 2, // Минимальное значение для типовых этажей (по умолчанию 2)
}) => {
  const effectiveIsEditable = isEditable;
  
  // Гарантируем, что maxFloorCount не меньше minTypicalFloor (защита от рассинхронизации данных)
  const effectiveMaxFloorCount = maxFloorCount !== null && maxFloorCount < minTypicalFloor 
    ? minTypicalFloor 
    : maxFloorCount;
  
  // Автоматический расчет количества типовых этажей на основе диапазона
  React.useEffect(() => {
    const calculatedCount = Math.max(0, typicalEnd - typicalStart + 1);
    // Обновляем только если вычисленное значение отличается от текущего
    if (calculatedCount !== typicalCount) {
      setTypicalCount(calculatedCount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typicalStart, typicalEnd]); // Убираем typicalCount и setTypicalCount из зависимостей, чтобы избежать циклов

  // Используем useRef для отслеживания предыдущего значения minTypicalFloor
  const prevMinTypicalFloorRef = React.useRef(minTypicalFloor);

  // Автоматически обновляем значения, если minTypicalFloor изменился
  React.useEffect(() => {
    // Обновляем только если minTypicalFloor действительно изменился
    if (prevMinTypicalFloorRef.current !== minTypicalFloor) {
      prevMinTypicalFloorRef.current = minTypicalFloor;

      // Обновляем только если текущие значения меньше нового минимума
      if (typicalStart < minTypicalFloor || typicalEnd < minTypicalFloor) {
        const newStart = Math.max(typicalStart, minTypicalFloor);
        const newEnd = Math.max(typicalEnd, minTypicalFloor, newStart);

        // Обновляем только если значения действительно изменились
        if (newStart !== typicalStart) {
          setTypicalStart(newStart);
        }
        if (newEnd !== typicalEnd) {
          setTypicalEnd(newEnd);
        }

        // Вызываем калькуляцию только если значения изменились
        if (newStart !== typicalStart || newEnd !== typicalEnd) {
          const updatedForm = {
            ...form,
            typicalStart: newStart,
            typicalEnd: newEnd,
            typicalCount: Math.max(0, newEnd - newStart + 1),
          };
          onTypicalCalcClick(updatedForm);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minTypicalFloor]);

  // Используем useRef для отслеживания предыдущего значения effectiveMaxFloorCount
  const prevMaxFloorCountRef = React.useRef(effectiveMaxFloorCount);

  // Автоматически ограничиваем typicalEnd, если effectiveMaxFloorCount изменился
  React.useEffect(() => {
    // Обновляем только если effectiveMaxFloorCount действительно изменился
    if (prevMaxFloorCountRef.current !== effectiveMaxFloorCount) {
      prevMaxFloorCountRef.current = effectiveMaxFloorCount;

      if (effectiveMaxFloorCount !== null) {
        let needsUpdate = false;
        let newStart = typicalStart;
        let newEnd = typicalEnd;

        if (typicalEnd > effectiveMaxFloorCount) {
          newEnd = Math.max(typicalStart, effectiveMaxFloorCount);
          needsUpdate = true;
        }
        if (typicalStart > effectiveMaxFloorCount) {
          newStart = Math.max(minTypicalFloor, effectiveMaxFloorCount);
          newEnd = Math.max(newStart, newEnd);
          needsUpdate = true;
        }

        // Обновляем только если значения действительно изменились
        if (
          needsUpdate &&
          (newStart !== typicalStart || newEnd !== typicalEnd)
        ) {
          if (newStart !== typicalStart) {
            setTypicalStart(newStart);
          }
          if (newEnd !== typicalEnd) {
            setTypicalEnd(newEnd);
          }

          const updatedForm = {
            ...form,
            typicalStart: newStart,
            typicalEnd: newEnd,
            typicalCount: Math.max(0, newEnd - newStart + 1),
          };
          onTypicalCalcClick(updatedForm);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveMaxFloorCount]);

  // Локальные обработчики с калькуляцией
  const handleStartChange = (val) => {
    let nextStart = val < minTypicalFloor ? minTypicalFloor : val; // Минимальное значение из нетипичных этажей + 1
    // Ограничиваем максимальным значением этажности
    if (effectiveMaxFloorCount !== null && nextStart > effectiveMaxFloorCount) {
      nextStart = effectiveMaxFloorCount;
    }
    setTypicalStart(nextStart);

    // Если начальный этаж больше конечного, обновляем конечный
    const newEnd = nextStart > typicalEnd ? nextStart : typicalEnd;
    if (nextStart > typicalEnd) {
      setTypicalEnd(nextStart);
    }

    // Синхронная калькуляция с актуальными значениями
    const updatedForm = {
      ...form,
      typicalStart: nextStart,
      typicalEnd: newEnd,
      typicalCount: Math.max(0, newEnd - nextStart + 1),
    };
    onTypicalCalcClick(updatedForm);
  };

  const handleEndChange = (val) => {
    let coerced =
      val < Math.max(minTypicalFloor, typicalStart)
        ? Math.max(minTypicalFloor, typicalStart)
        : val;
    // Ограничиваем максимальным значением этажности
    if (effectiveMaxFloorCount !== null && coerced > effectiveMaxFloorCount) {
      coerced = effectiveMaxFloorCount;
    }
    setTypicalEnd(coerced);

    // Синхронная калькуляция
    const updatedForm = {
      ...form,
      typicalEnd: coerced,
      typicalCount: Math.max(0, coerced - typicalStart + 1), // Обновляем количество этажей
    };
    onTypicalCalcClick(updatedForm);
  };

  // Локальный редактируемый инпут (в стиле WorkTab)
  const InlineEditableLocal = ({
    value,
    onSave,
    width = 120,
    inputProps = {},
    isEditableLocal = isEditable,
  }) => {
    const [isEditing, setIsEditing] = React.useState(false);
    const [localValue, setLocalValue] = React.useState(value);
    const inputRef = React.useRef(null);
    const computeWidthPx = (val) => {
      const text =
        val === undefined || val === null || val === "" ? "0" : String(val);
      const charCount = text.length;
      return Math.max(30, charCount * 10 + 16);
    };
    React.useEffect(() => setLocalValue(value), [value]);
    React.useEffect(() => {
      if (isEditing && inputRef.current) {
        inputRef.current.focus();
        const input = inputRef.current;
        requestAnimationFrame(() => {
          try {
            input.select();
          } catch {}
        });
      }
    }, [isEditing]);
    if (isEditing) {
      const wContent = computeWidthPx(localValue);
      const w = Math.max(width, wContent);
      return (
        <div
          style={{
            position: "relative",
            width: `${w}px`,
            margin: 0,
            display: "inline-block",
          }}
        >
          <input
            {...{
              type: "number",
              step: "0.01",
              min: "0",
              inputMode: "decimal",
              ...inputProps,
            }}
            value={localValue}
            ref={inputRef}
            onChange={(e) => setLocalValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                // Убираем ведущие нули из числовых значений
                const cleanValue =
                  localValue === "" ? "0" : String(Number(localValue));
                onSave(cleanValue);
                setIsEditing(false);
              } else if (e.key === "Escape") {
                setLocalValue(value);
                setIsEditing(false);
              }
            }}
            style={{
              width: `${w}px`,
              padding: "6px 10px",
              border: "1.5px dashed #B8C2D1",
              borderRadius: 6,
              background: "#FFFFFF",
              fontSize: 15,
              fontFamily: "Manrope, sans-serif",
              textAlign: "left",
              outline: "none",
              transition: "border-color 0.2s",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#007bff")}
            onBlur={(e) => {
              e.target.style.borderColor = "#ccc";
              // Автоматически сохраняем при потере фокуса
              const cleanValue =
                localValue === "" ? "0" : String(Number(localValue));
              onSave(cleanValue);
              setIsEditing(false);
            }}
            disabled={!isEditableLocal}
          />
        </div>
      );
    }
    const wViewContent = computeWidthPx(value ?? "0");
    const wView = Math.max(width, wViewContent);
    return (
      <div
        style={{
          width: `${wView}px`,
          margin: 0,
          textAlign: "left",
          display: "inline-block",
        }}
      >
        <span
          style={{
            fontSize: 15,
            fontFamily: "Manrope, sans-serif",
            display: "inline-block",
            width: `${wView}px`,
            padding: "6px 10px",
            border: "1.5px dashed #B8C2D1",
            borderRadius: 6,
            background: !isEditableLocal ? "#F4F6FA" : "#F9FBFF",
            whiteSpace: "nowrap",
            cursor: !isEditableLocal ? "default" : "pointer",
            textAlign: "left",
            height: "16px",
          }}
          onClick={() => (!isEditableLocal ? undefined : setIsEditing(true))}
        >
          {value}
        </span>
      </div>
    );
  };

  return (
    <div className="typical-floors-form" style={{ position: "relative" }}>
      <button
        type="button"
        className="close-typical-form-btn"
        onClick={onClose}
        disabled={!effectiveIsEditable}
        style={{
          position: "absolute",
          top: 8,
          right: 8,
          background: "none",
          border: "none",
          fontSize: 22,
          cursor: !isEditable ? "not-allowed" : "pointer",
          color: !isEditable ? "#ccc" : "#7B8AAB",
          zIndex: 2,
          opacity: !isEditable ? 0.5 : 1,
        }}
        aria-label="Закрыть"
      >
        ×
      </button>
      <div className="form-group form-group-full section-title-row">
        <div className="section-caption">Типовые этажи</div>
      </div>
      <div className="form-group form-group-full">
        <label>Выберите типовые этажи*</label>
        <div className="typical-floors-range">
          <span>с:</span>
          <NumberSelect
            value={typicalStart}
            onChange={handleStartChange}
            options={(() => {
              const rawLength =
                effectiveMaxFloorCount != null
                  ? effectiveMaxFloorCount - minTypicalFloor + 1
                  : 50 - minTypicalFloor + 1;
              const length = Math.max(1, rawLength);
              return Array.from({ length }, (_, i) => i + minTypicalFloor);
            })()}
            minValue={minTypicalFloor}
            maxValue={effectiveMaxFloorCount}
            isEditable={effectiveIsEditable}
            placeholder="с:"
          />
          <span>по:</span>
          <NumberSelect
            value={typicalEnd}
            onChange={handleEndChange}
            options={(() => {
              const rawLength =
                effectiveMaxFloorCount != null
                  ? effectiveMaxFloorCount - minTypicalFloor + 1
                  : 50 - minTypicalFloor + 1;
              const length = Math.max(1, rawLength);
              return Array.from({ length }, (_, i) => i + minTypicalFloor);
            })()}
            minValue={typicalStart}
            maxValue={effectiveMaxFloorCount}
            isEditable={effectiveIsEditable}
            placeholder="по:"
          />
        </div>
      </div>
      <div className="form-group form-group-full two-col-row">
        <div className="left-col">
          <label>Общая площадь типовых этажей (м2)</label>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span
              style={{
                fontSize: "15px",
                fontFamily: "Manrope, sans-serif",
                minWidth: "30px",
                padding: "6px 10px",
                border: "1.5px solid #E6E8EC",
                borderRadius: "10px",
                background: "#F4F6FA",
              }}
            >
              {smartRound(parseFloat(typicalArea) || 0)}
            </span>
          </div>
        </div>
        <div className="right-col vertical-group">
          <div className="form-group">
            <label>Введите площадь одного этажа (м2)*</label>
            <InlineEditableWithMath
              value={oneFloorArea}
              isEditable={effectiveIsEditable}
              onSave={(v) => {
                setOneFloorArea(v);
                // Синхронная калькуляция
                const updatedForm = {
                  ...form,
                  oneFloorArea: v,
                };
                onTypicalCalcClick(updatedForm);
              }}
              width={80}
            />
          </div>
          <div className="form-group">
            <label>Количество типовых этажей (автоматически)</label>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span
                style={{
                  fontSize: "15px",
                  fontFamily: "Manrope, sans-serif",
                  minWidth: "30px",
                  padding: "6px 10px",
                  border: "1.5px solid #E6E8EC",
                  borderRadius: "10px",
                  background: "#F4F6FA",
                  color: "#666",
                }}
              >
                {Math.max(0, typicalEnd - typicalStart + 1)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CREATE_NEW_BLOCK_OPTION = { value: "__create_new__", label: "+ Создать новый блок" };

const InitialData = ({ projectId, projectLockedByApproval = false }) => {
  // Убеждаемся, что projectId является числом
  const numericProjectId = parseInt(projectId);
  const [activeBlockTab, setActiveBlockTab] = useState(null);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [deletedBlockName, setDeletedBlockName] = useState("");
  const [showAddBlockPopup, setShowAddBlockPopup] = useState(false);
  const [blocksList, setBlocksList] = useState([]);
  const [allBlocksList, setAllBlocksList] = useState([]); // все блоки из API для проверки уникальности имени
  const [selectedBlocks, setSelectedBlocks] = useState([]);
  const [isLoadingBlocks, setIsLoadingBlocks] = useState(false);
  const [isCreatingNewBlock, setIsCreatingNewBlock] = useState(false);
  const [newBlockName, setNewBlockName] = useState("");
  const [newBlockNameError, setNewBlockNameError] = useState("");
  const [isSubmittingAddBlock, setIsSubmittingAddBlock] = useState(false);
  const [infoTypes, setInfoTypes] = useState([]);
  const [isLoadingInfoTypes, setIsLoadingInfoTypes] = useState(false);
  const [engrooms, setEngrooms] = useState([]);
  const [isLoadingEngrooms, setIsLoadingEngrooms] = useState(false);
  const [projectData, setProjectData] = useState([]);
  const [isLoadingProjectData, setIsLoadingProjectData] = useState(false);
  const [total, setTotal] = useState("0");
  const [typicalFloorsForms, setTypicalFloorsForms] = useState([]);
  const [blockTypicalFloors, setBlockTypicalFloors] = useState({});
  const [formData, setFormData] = useState({});
  const [lastCalculatedTotal, setLastCalculatedTotal] = useState({}); // Храним последние рассчитанные значения для каждого блока
  const [showSuccessPopup, setShowSuccessPopup] = useState(false); // Состояние для успешного попапа
  const [successMessage, setSuccessMessage] = useState(""); // Сообщение для попапа
  const [localBlocks, setLocalBlocks] = useState([]); // Локальные блоки, которые еще не сохранены в базе
  const localBlocksRef = React.useRef([]); // Ref для доступа к актуальным локальным блокам
  const [nextLocalId, setNextLocalId] = useState(-1); // Счетчик для локальных ID

  // Синхронизируем ref с состоянием
  React.useEffect(() => {
    localBlocksRef.current = localBlocks;
  }, [localBlocks]);
  // Новые локальные состояния под макет (без API)
  const [plotAreaHa, setPlotAreaHa] = useState("0"); // площадь участка в гектарах
  const [applyFloorCoefficient, setApplyFloorCoefficient] = useState(false);
  const [applyAccelerationCoefficient, setApplyAccelerationCoefficient] =
    useState(false);
  const [applyBimCoefficient, setApplyBimCoefficient] = useState(false);
  const [smetaDocumentationPercent, setSmetaDocumentationPercent] =
    useState("0"); // Ставка за разработку сметной документации
  const [blocksSchemaImageUrl, setBlocksSchemaImageUrl] = useState(""); // URL схемы блокировки
  const [isUploadingImage, setIsUploadingImage] = useState(false); // Состояние загрузки изображения
  const [isSavingAllBlocks, setIsSavingAllBlocks] = useState(false); // Состояние загрузки при сохранении всех блоков
  const [showImagePopup, setShowImagePopup] = useState(false); // Состояние попапа с изображением

  const [projectNotFound, setProjectNotFound] = useState(false); // Состояние для проектов без данных
  const [hasServerError, setHasServerError] = useState(false); // Состояние для ошибки сервера
  const [isEditable, setIsEditable] = useState(true); // Флаг редактируемости ТЭПов
  const [editingMessage, setEditingMessage] = useState(null); // Сообщение о блокировке редактирования из API
  const [cpApprovalStatus, setCpApprovalStatus] = useState(null); // Статус согласования КП
  const [peApprovalStatus, setPeApprovalStatus] = useState(null); // Статус согласования Сметы
  const [isCreatingCopy, setIsCreatingCopy] = useState(false); // Флаг создания копии
  const [version, setVersion] = useState(null); // Версия ТЭП
  const [teisApprovals, setTeisApprovals] = useState([]); // Список согласований ТЭП с версиями

  // При активном согласовании сметы или КП блокируем редактирование (флаг с родительской страницы)
  const effectiveIsEditable = isEditable && !projectLockedByApproval;

  // Обработчик клавиши Escape для закрытия попапа с изображением
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && showImagePopup) {
        setShowImagePopup(false);
      }
    };

    if (showImagePopup) {
      document.addEventListener("keydown", handleEscape);
      // Блокируем прокрутку страницы
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [showImagePopup]);

  // Прокрутка к нижнему краю страницы при раскрытии блока
  useEffect(() => {
    if (activeBlockTab !== null) {
      // Используем setTimeout чтобы дать время на рендеринг контента
      setTimeout(() => {
        window.scrollTo({
          top: document.documentElement.scrollHeight,
          behavior: "smooth",
        });
      }, 100);
    }
  }, [activeBlockTab]);

  // Мок-данные для коэффициента и ставки по каждому блоку
  const [blockExtras, setBlockExtras] = useState({}); // { [blockId]: { coefficient: number, rate: number } }
  const [editingCoeff, setEditingCoeff] = useState({}); // { [blockId]: boolean }
  const [tempCoeff, setTempCoeff] = useState({}); // { [blockId]: string|number }
  const [editingRate, setEditingRate] = useState({}); // { [blockId]: boolean }
  const [tempRate, setTempRate] = useState({}); // { [blockId]: string|number }

  const startEditCoeff = (blockId) => {
    setEditingCoeff((prev) => ({ ...prev, [blockId]: true }));
    setTempCoeff((prev) => ({
      ...prev,
      [blockId]: blockExtras[blockId]?.coefficient ?? 0,
    }));
  };
  const cancelEditCoeff = (blockId) => {
    setEditingCoeff((prev) => ({ ...prev, [blockId]: false }));
    setTempCoeff((prev) => ({
      ...prev,
      [blockId]: blockExtras[blockId]?.coefficient ?? 0,
    }));
  };
  const saveEditCoeff = (blockId, value) => {
    const numeric = parseFloat(value);
    setBlockExtras((prev) => ({
      ...prev,
      [blockId]: {
        ...(prev[blockId] || {}),
        coefficient: isNaN(numeric) ? 0 : numeric,
      },
    }));
    setEditingCoeff((prev) => ({ ...prev, [blockId]: false }));
  };

  const startEditRate = (blockId) => {
    setEditingRate((prev) => ({ ...prev, [blockId]: true }));
    setTempRate((prev) => ({
      ...prev,
      [blockId]: blockExtras[blockId]?.rate ?? 0,
    }));
  };
  const cancelEditRate = (blockId) => {
    setEditingRate((prev) => ({ ...prev, [blockId]: false }));
    setTempRate((prev) => ({
      ...prev,
      [blockId]: blockExtras[blockId]?.rate ?? 0,
    }));
  };
  const saveEditRate = (blockId, value) => {
    const numeric = parseFloat(value);
    setBlockExtras((prev) => ({
      ...prev,
      [blockId]: {
        ...(prev[blockId] || {}),
        rate: isNaN(numeric) ? 0 : numeric,
      },
    }));
    setEditingRate((prev) => ({ ...prev, [blockId]: false }));
  };

  // Функция для установки коэффициента на основе типа секции
  const setCoefficientBySectionType = React.useCallback(
    (blockId, infTypeId) => {
      if (!infTypeId) return;

      const sectionType = infoTypes.find((type) => type.id === infTypeId);
      if (!sectionType) return;

      let coefficient = 0;
      const typeName = sectionType.name?.toLowerCase();

      if (typeName === "базовый") {
        coefficient = 1.0;
      } else if (typeName === "зеркало") {
        coefficient = 0.7;
      } else if (typeName === "привязка") {
        coefficient = 0.3;
      }

      setBlockExtras((prev) => ({
        ...prev,
        [blockId]: {
          ...(prev[blockId] || {}),
          coefficient: coefficient,
        },
      }));
    },
    [infoTypes]
  );

  // Функция для пересчета общей суммы
  // Использует только данные с бэкенда (lastCalculatedTotal) или данные блоков
  // Вся калькуляция должна выполняться на бэкенде через API calculateTotalArea
  const recalculateTotal = () => {
    // Объединяем все блоки для расчета общей суммы
    const allBlocksForCalculation = [...projectData, ...localBlocks];

    // Используем рассчитанную площадь от API или данные блока
    const totalArea = allBlocksForCalculation.reduce((sum, block) => {
      // Приоритет: lastCalculatedTotal > formData.total_area > block.total_area
      const blockTotalArea =
        lastCalculatedTotal[block.id] !== undefined
          ? lastCalculatedTotal[block.id]
          : formData[block.id]?.total_area !== undefined
          ? parseNumericValue(formData[block.id].total_area.toString())
          : block.total_area || 0;
      return sum + blockTotalArea;
    }, 0);
    setTotal(totalArea.toString());
  };

  // Функция для обработки типичных этажей с сохранением локальных форм
  const processTypicalFloors = (
    projectDataResponse,
    preserveLocalForms = true
  ) => {
    setBlockTypicalFloors((prev) => {
      const typicalFloorsByBlock = { ...prev }; // Сохраняем существующие данные
      const currentLocalBlocks = localBlocksRef.current; // Используем актуальные локальные блоки

      // Обрабатываем сохраненные блоки из API
      projectDataResponse.forEach((block) => {
        if (block.typical_floors && block.typical_floors.length > 0) {
          typicalFloorsByBlock[block.id] = block.typical_floors.map(
            (floor, index) => ({
              id: Date.now() + index + Math.random(),
              typicalStart: floor.floor_number_from,
              typicalEnd: floor.floor_number_to,
              typicalArea:
                floor.total_area?.toString() || floor.area.toString(),
              oneFloorArea: floor.area.toString(),
              typicalCount: floor.floors_count || 1,
            })
          );
        } else {
          if (preserveLocalForms) {
            // Сохраняем локальные формы типовых этажей, если они есть
            // НЕ перезаписываем, если данные уже есть
            if (prev[block.id] && prev[block.id].length > 0) {
              typicalFloorsByBlock[block.id] = prev[block.id];
            } else if (!typicalFloorsByBlock[block.id]) {
              // Если данных нет, создаем пустой массив только если его еще нет
              typicalFloorsByBlock[block.id] = [];
            }
          } else {
            // При первоначальной загрузке НЕ перезаписываем существующие данные
            if (!typicalFloorsByBlock[block.id]) {
              typicalFloorsByBlock[block.id] = [];
            }
          }
        }
      });

      // Сохраняем данные типовых этажей для локальных блоков, если они есть
      currentLocalBlocks.forEach((localBlock) => {
        if (prev[localBlock.id] && prev[localBlock.id].length > 0) {
          typicalFloorsByBlock[localBlock.id] = prev[localBlock.id];
        } else if (!typicalFloorsByBlock[localBlock.id]) {
          typicalFloorsByBlock[localBlock.id] = [];
        }
      });

      return typicalFloorsByBlock;
    });
  };

  // Загрузка данных проекта и справочников при монтировании компонента
  useEffect(() => {
    const loadData = async () => {
      // Устанавливаем все загрузки в true одновременно
      setIsLoadingProjectData(true);
      setIsLoadingInfoTypes(true);
      setIsLoadingEngrooms(true);

      try {
        // Запускаем все загрузки параллельно
        const promises = [];

        // Загрузка данных проекта только если projectId существует
        if (numericProjectId) {
          promises.push(
            (async () => {
              try {
                // Загружаем данные проекта и список согласований параллельно
                const [projectDataResponse, approvals] = await Promise.all([
                  getCpTeis(numericProjectId),
                  getProjectApprovalStatuses(numericProjectId).catch(() => []),
                ]);

                // Сохраняем список согласований
                setTeisApprovals(approvals || []);

                // Извлекаем блоки из нового формата ответа
                const blocks = projectDataResponse.blocks || [];

                setProjectData(blocks);
                setProjectNotFound(false); // Сбрасываем состояние, так как данные найдены

                // Устанавливаем статусы согласований из API
                setCpApprovalStatus(
                  projectDataResponse.cp_approval_status || null
                );
                setPeApprovalStatus(
                  projectDataResponse.pe_approval_status || null
                );

                // Получаем текущую версию из данных ТЭП
                const currentVersion = projectDataResponse?.version;

                // Получаем максимальную версию из списка согласований ТЭП
                const approvalsList = approvals || [];
                const maxApprovalVersion =
                  approvalsList.length > 0
                    ? Math.max(
                        ...approvalsList
                          .map((a) => a.version)
                          .filter((v) => v !== null && v !== undefined)
                      )
                    : null;

                // Сравниваем версии: если текущая версия больше максимальной из списка согласований,
                // то можно не блокировать редактирование
                const isVersionNewer =
                  currentVersion !== null &&
                  currentVersion !== undefined &&
                  maxApprovalVersion !== null &&
                  currentVersion > maxApprovalVersion;

                // Определяем флаг редактируемости на основе статусов согласований
                // Если статус "согласован" (approved или "согласован"), блокируем редактирование,
                // но не блокируем если версия новее максимальной из списка согласований
                const cpStatus = projectDataResponse.cp_approval_status;
                const peStatus = projectDataResponse.pe_approval_status;
                const isApproved =
                  cpStatus === "approved" ||
                  cpStatus === "согласован" ||
                  peStatus === "approved" ||
                  peStatus === "согласован";

                // Устанавливаем флаг редактируемости из API или на основе статусов
                // Если версия новее, разрешаем редактирование
                setIsEditable(
                  (projectDataResponse.editable !== false || isVersionNewer) &&
                    (!isApproved || isVersionNewer)
                );

                // Устанавливаем сообщение о блокировке редактирования из API
                setEditingMessage(projectDataResponse.message || null);

                // Устанавливаем значения переключателей из ответа API
                if (projectDataResponse.apply_floor_coefficient !== undefined) {
                  setApplyFloorCoefficient(
                    projectDataResponse.apply_floor_coefficient
                  );
                }
                if (
                  projectDataResponse.apply_acceleration_coefficient !==
                  undefined
                ) {
                  setApplyAccelerationCoefficient(
                    projectDataResponse.apply_acceleration_coefficient
                  );
                }
                if (
                  projectDataResponse.apply_bim_surcharge_coefficient !==
                  undefined
                ) {
                  setApplyBimCoefficient(
                    projectDataResponse.apply_bim_surcharge_coefficient
                  );
                }

                // Устанавливаем ставку за разработку сметной документации из ответа API
                if (
                  projectDataResponse.smeta_documentation_percent !== undefined
                ) {
                  setSmetaDocumentationPercent(
                    projectDataResponse.smeta_documentation_percent?.toString() ||
                      "0"
                  );
                }

                // Устанавливаем площадь участка из ответа API
                if (projectDataResponse.hec_land_area !== undefined) {
                  setPlotAreaHa(
                    projectDataResponse.hec_land_area?.toString() || "0"
                  );
                }

                // Устанавливаем версию из ответа API
                if (
                  projectDataResponse.version !== undefined &&
                  projectDataResponse.version !== null
                ) {
                  setVersion(projectDataResponse.version);
                }

                // Устанавливаем URL схемы блокировки из ответа API
                if (projectDataResponse.blocks_schema_image_url) {
                  // Проверяем, является ли URL полным или относительным
                  let imageUrl = projectDataResponse.blocks_schema_image_url;
                  if (!imageUrl.startsWith("http")) {
                    // Если это не полный URL, формируем правильный путь для изображения
                    if (imageUrl.startsWith("/")) {
                      // Если путь начинается с /, добавляем базовый URL без дополнительного /
                      imageUrl = `${getAPIURL().replace(/\/$/, "")}${imageUrl}`;
                    } else {
                      // Если это только имя файла, формируем полный путь к изображению проекта
                      // На локальной машине используем другой путь
                      const isLocalhost =
                        window.location.hostname === "localhost" ||
                        window.location.hostname === "127.0.0.1";
                      if (isLocalhost) {
                        // На локальной машине используем старый путь
                        imageUrl = `${getAPIURL()}images/blocks_schema/${imageUrl}`;
                      } else {
                        // На серверах используем новый путь
                        // Проверяем, является ли это тестовым окружением
                        const isTestEnv = process.env.REACT_APP_ENV === "test";
                        if (isTestEnv) {
                          // Для тестового окружения используем полный URL без дублирования
                          imageUrl = `https://test.portal.akmolpro.kz/api/images/blocks_schema/${imageUrl}`;
                        } else {
                          // На продакшн серверах используем новый путь
                          const isProdEnv =
                            process.env.REACT_APP_ENV === "prod";
                          if (isProdEnv) {
                            // Для продакшн окружения используем полный URL без дублирования
                            imageUrl = `https://portal.akmolpro.kz/api/images/blocks_schema/${imageUrl}`;
                          } else {
                            // Для других серверов используем новый путь
                            imageUrl = `${getAPIURL()}project_estimates/cp_teis/${numericProjectId}/images/${imageUrl}`;
                          }
                        }
                      }
                    }
                  }

                  setBlocksSchemaImageUrl(imageUrl);
                } else {
                  // Если URL нет, сбрасываем состояния
                  setBlocksSchemaImageUrl("");
                }

                // Очищаем последние рассчитанные значения при загрузке новых данных
                setLastCalculatedTotal({});

                // Обновляем общую сумму по всем блокам
                const totalArea = blocks.reduce(
                  (sum, block) => sum + (block.total_area || 0),
                  0
                );
                setTotal(totalArea.toString());

                // Обрабатываем типичные этажи для каждого блока
                processTypicalFloors(blocks, false);

                // Не выбираем активный блок автоматически при загрузке данных
              } catch (error) {
                console.error("Error loading project data:", error);

                // Проверяем, является ли это ошибкой 404 (проект не найден) или 500 (серверная ошибка)
                if (error.message.includes("404")) {
                  setProjectNotFound(true);
                  setProjectData([]);
                } else if (error.message.includes("500")) {
                  setHasServerError(true);
                  setProjectNotFound(false);
                  setProjectData([]);
                } else {
                  setProjectNotFound(false);
                  setProjectData([]);
                }
              }
            })()
          );
        }

        // Загрузка типов информации
        promises.push(
          (async () => {
            try {
              const infoTypesData = await getCpTeiInfTypes();
              setInfoTypes(infoTypesData);
            } catch (error) {
              console.error("Error loading info types:", error);
              setInfoTypes([]);
            }
          })()
        );

        // Загрузка типов инженерных помещений
        promises.push(
          (async () => {
            try {
              const engroomsData = await getCpTeiEngrooms();
              setEngrooms(engroomsData);
            } catch (error) {
              console.error("Error loading engrooms:", error);
              setEngrooms([]);
            }
          })()
        );

        // Ждем завершения всех загрузок
        await Promise.all(promises);
      } finally {
        // Сбрасываем все состояния загрузки одновременно
        setIsLoadingProjectData(false);
        setIsLoadingInfoTypes(false);
        setIsLoadingEngrooms(false);
      }
    };

    loadData();
  }, [projectId]);

  // Пересчитываем isEditable при изменении версии или списка согласований
  // Это нужно для случаев, когда список согласований обновляется после загрузки данных проекта
  useEffect(() => {
    if (!numericProjectId || version === null) return;

    const recalculateEditable = async () => {
      try {
        const projectDataResponse = await getCpTeis(numericProjectId);

        // Получаем текущую версию из данных ТЭП
        const currentVersion = projectDataResponse?.version || version;

        // Получаем максимальную версию из списка согласований ТЭП
        const maxApprovalVersion =
          teisApprovals && teisApprovals.length > 0
            ? Math.max(
                ...teisApprovals
                  .map((a) => a.version)
                  .filter((v) => v !== null && v !== undefined)
              )
            : null;

        // Сравниваем версии: если текущая версия больше максимальной из списка согласований,
        // то можно не блокировать редактирование
        const isVersionNewer =
          currentVersion !== null &&
          currentVersion !== undefined &&
          maxApprovalVersion !== null &&
          currentVersion > maxApprovalVersion;

        // Определяем флаг редактируемости на основе статусов согласований
        const cpStatus = projectDataResponse.cp_approval_status;
        const peStatus = projectDataResponse.pe_approval_status;
        const isApproved =
          cpStatus === "approved" ||
          cpStatus === "согласован" ||
          peStatus === "approved" ||
          peStatus === "согласован";

        // Устанавливаем флаг редактируемости из API или на основе статусов
        setIsEditable(
          (projectDataResponse.editable !== false || isVersionNewer) &&
            (!isApproved || isVersionNewer)
        );
      } catch (error) {
        console.error("Ошибка при пересчете редактируемости:", error);
      }
    };

    recalculateEditable();
  }, [version, teisApprovals, numericProjectId]);

  // Сохраняем данные типовых этажей при переключении блоков
  useEffect(() => {
    // При переключении блоков данные типовых этажей уже сохранены в blockTypicalFloors
    // Этот useEffect просто гарантирует, что данные не теряются и не перезаписываются
    if (activeBlockTab !== null) {
      setBlockTypicalFloors((prev) => {
        // НЕ перезаписываем существующие данные, только создаем пустой массив если его нет
        if (!prev[activeBlockTab]) {
          const updated = {
            ...prev,
            [activeBlockTab]: [],
          };
          return updated;
        }
        // Сохраняем все существующие данные без изменений
        return prev;
      });
    }
  }, [activeBlockTab]);

  // Инициализация и поддержание данных коэффициентов/ставок при изменении списка блоков
  useEffect(() => {
    const all = [...projectData, ...localBlocks];
    if (all.length === 0) return;
    setBlockExtras((prev) => {
      const next = { ...prev };
      all.forEach((block) => {
        if (next[block.id]) return;
        // Используем данные из API, если они есть, иначе значения по умолчанию
        const coefficient =
          block.coefficient !== null && block.coefficient !== undefined
            ? block.coefficient
            : 0;
        const rate =
          block.price_per_m2 !== null && block.price_per_m2 !== undefined
            ? block.price_per_m2
            : 0;

        next[block.id] = {
          coefficient: coefficient,
          rate: rate,
        };
      });
      return next;
    });
  }, [projectData, localBlocks]);

  // Устанавливаем коэффициент на основе типа секции для блоков с коэффициентом 0
  useEffect(() => {
    if (!infoTypes || infoTypes.length === 0) return;

    const all = [...projectData, ...localBlocks];
    if (all.length === 0) return;

    setBlockExtras((prev) => {
      const next = { ...prev };
      let hasChanges = false;

      all.forEach((block) => {
        // Получаем inf_type_id из formData или из блока
        const formDataForBlock = formData[block.id];
        const infTypeId = formDataForBlock?.inf_type_id || block.inf_type?.id;

        // Если коэффициент равен 0 или не установлен, и есть тип секции, устанавливаем коэффициент
        if (
          (!next[block.id]?.coefficient || next[block.id].coefficient === 0) &&
          infTypeId
        ) {
          const sectionType = infoTypes.find((type) => type.id === infTypeId);
          if (sectionType) {
            let coefficient = 0;
            const typeName = sectionType.name?.toLowerCase();

            if (typeName === "базовый") {
              coefficient = 1.0;
            } else if (typeName === "зеркало") {
              coefficient = 0.7;
            } else if (typeName === "привязка") {
              coefficient = 0.3;
            }

            if (coefficient > 0) {
              if (!next[block.id]) {
                next[block.id] = { coefficient: 0, rate: 0 };
              }
              next[block.id].coefficient = coefficient;
              hasChanges = true;
            }
          }
        }
      });

      return hasChanges ? next : prev;
    });
  }, [projectData, localBlocks, infoTypes, formData]);

  const handleDeleteClick = (blockName) => {
    if (!effectiveIsEditable) return; // Не открываем попап удаления если редактирование заблокировано
    setDeletedBlockName(blockName);
    setShowDeletePopup(true);
  };

  const handleCancelDelete = () => {
    setShowDeletePopup(false);
    setDeletedBlockName("");
  };

  const handleConfirmDelete = async () => {
    if (!effectiveIsEditable) return; // Не удаляем если редактирование заблокировано
    try {
      // Проверяем, является ли текущий блок локальным
      const currentLocalBlock = localBlocks.find(
        (b) => b.id === activeBlockTab
      );
      const currentBlockData = projectData.find((b) => b.id === activeBlockTab);

      if (!currentLocalBlock && !currentBlockData) {
        console.error("Текущий блок не найден");
        return;
      }

      if (currentLocalBlock) {
        // Если это локальный блок, просто удаляем его из локального состояния

        setLocalBlocks((prev) => {
          const updatedLocalBlocks = prev.filter(
            (b) => b.id !== currentLocalBlock.id
          );

          // Устанавливаем активный блок на первый оставшийся блок
          const remainingBlocks = [...projectData, ...updatedLocalBlocks];
          if (remainingBlocks.length > 0) {
            setActiveBlockTab(remainingBlocks[0].id);
          } else {
            setActiveBlockTab(null);
          }

          return updatedLocalBlocks;
        });

        // Очищаем связанные данные для удаленного блока
        setFormData((prev) => {
          const { [currentLocalBlock.id]: removed, ...rest } = prev;
          return rest;
        });

        setBlockTypicalFloors((prev) => {
          const { [currentLocalBlock.id]: removed, ...rest } = prev;
          return rest;
        });

        setLastCalculatedTotal((prev) => {
          const { [currentLocalBlock.id]: removed, ...rest } = prev;
          return rest;
        });

        setSuccessMessage("Локальный блок удален");
      } else {
        // Если это сохраненный блок, удаляем его из базы данных

        // Вызываем API для удаления блока
        await deleteCpTei(currentBlockData.id);

        // Перезагружаем данные проекта
        try {
          const updatedProjectDataResponse = await getCpTeis(numericProjectId);
          const updatedBlocks = updatedProjectDataResponse.blocks || [];
          setProjectData(updatedBlocks);

          // Обновляем версию
          if (
            updatedProjectDataResponse.version !== undefined &&
            updatedProjectDataResponse.version !== null
          ) {
            setVersion(updatedProjectDataResponse.version);
          }

          // Обрабатываем типичные этажи для оставшихся блоков
          processTypicalFloors(updatedBlocks, true);

          // Устанавливаем активный блок на первый оставшийся блок
          if (updatedBlocks.length > 0) {
            setActiveBlockTab(updatedBlocks[0].id);
          } else {
            setActiveBlockTab(null);
          }
        } catch (error) {
          // Если получили 404, значит проект больше не существует или пуст
          if (error.message.includes("404")) {
            setProjectData([]);
            setBlockTypicalFloors({});
            setProjectNotFound(true);
            setActiveBlockTab(null);
          } else {
            // Для других ошибок обновляем локально, убирая удаленный блок
            setProjectData((prev) => {
              const remainingBlocks = prev.filter(
                (block) => block.id !== currentBlockData.id
              );

              // Устанавливаем активный блок на первый оставшийся блок
              if (remainingBlocks.length > 0) {
                setActiveBlockTab(remainingBlocks[0].id);
              } else {
                setActiveBlockTab(null);
              }

              return remainingBlocks;
            });

            // Обрабатываем типичные этажи для оставшихся блоков
            const remainingBlocks = projectData.filter(
              (block) => block.id !== currentBlockData.id
            );
            processTypicalFloors(remainingBlocks, true);
          }
        }

        // Очищаем связанные данные для удаленного блока
        setFormData((prev) => {
          const { [currentBlockData.id]: removed, ...rest } = prev;
          return rest;
        });

        setLastCalculatedTotal((prev) => {
          const { [currentBlockData.id]: removed, ...rest } = prev;
          return rest;
        });

        setSuccessMessage("Блок успешно удален из базы данных");
      }

      // Показываем успешный попап
      setShowSuccessPopup(true);
      setTimeout(() => {
        setShowSuccessPopup(false);
      }, 3000);

      setShowDeletePopup(false);
      setDeletedBlockName("");

      // Дополнительная проверка через небольшую задержку
      setTimeout(() => {
        // Проверка завершена
      }, 100);
    } catch (error) {
      console.error("Ошибка при удалении блока:", error);

      // Показываем пользователю подробную информацию об ошибке
      let errorMessage = "Ошибка при удалении блока";

      if (error.message.includes("500")) {
        errorMessage = "Ошибка сервера (500). Попробуйте позже.";
      } else if (error.message.includes("400")) {
        errorMessage = "Ошибка валидации данных (400): " + error.message;
      } else if (error.message.includes("401")) {
        errorMessage =
          "Ошибка авторизации (401). Попробуйте перезайти в систему.";
      } else if (error.message.includes("403")) {
        errorMessage =
          "Ошибка доступа (403). У вас нет прав для удаления этого блока.";
      } else if (error.message.includes("404")) {
        errorMessage = "Блок не найден (404). Возможно, он уже был удален.";
      } else {
        errorMessage = "Неизвестная ошибка: " + error.message;
      }

      setSuccessMessage(errorMessage);
      setShowSuccessPopup(true);
      setTimeout(() => {
        setShowSuccessPopup(false);
      }, 5000);
    }
  };

  const handleAddBlockClick = async () => {
    if (!effectiveIsEditable) return; // Не открываем попап если редактирование заблокировано
    setShowAddBlockPopup(true);
    setIsCreatingNewBlock(false);
    setNewBlockName("");
    setNewBlockNameError("");
    setIsLoadingBlocks(true);
    try {
      const data = await getAllBlocks();

      // Получаем ID уже добавленных блоков (из базы и локальных)
      const existingBlockIds = new Set();

      // Добавляем ID из сохраненных блоков
      projectData.forEach((block) => {
        if (block.block && block.block.id) {
          existingBlockIds.add(block.block.id);
        }
      });

      // Добавляем ID из локальных блоков
      localBlocks.forEach((block) => {
        existingBlockIds.add(block.block_id);
      });

      // Фильтруем только те блоки, которые еще не добавлены
      const availableBlocks = data
        .filter((block) => block.is_active && !existingBlockIds.has(block.id))
        .map((block) => ({ value: block.id, label: block.name }));

      setAllBlocksList(data || []);
      setBlocksList(availableBlocks);
    } catch (e) {
      setBlocksList([]);
      setAllBlocksList([]);
    } finally {
      setIsLoadingBlocks(false);
    }
  };

  const handleCancelAddBlock = () => {
    setShowAddBlockPopup(false);
    setSelectedBlocks([]);
    setIsCreatingNewBlock(false);
    setNewBlockName("");
    setNewBlockNameError("");
  };

  const handleNewBlockNameChange = (e) => {
    const value = e.target.value;
    setNewBlockName(value);
    setNewBlockNameError("");
    if (value.trim()) {
      const valueLower = value.toLowerCase().trim();
      const existsInList = (allBlocksList || []).some(
        (b) => (b.name || "").toLowerCase().trim() === valueLower
      );
      if (existsInList) {
        setNewBlockNameError("Блок с таким названием уже есть в справочнике. Выберите его из списка.");
      }
    }
  };

  const handleConfirmAddBlock = async () => {
    try {
      if (isCreatingNewBlock) {
        const name = newBlockName.trim();
        if (!name) {
          setNewBlockNameError("Введите название блока");
          return;
        }
        if (newBlockNameError) return;

        setIsSubmittingAddBlock(true);
        setNewBlockNameError("");
        try {
          // Создаем блок только в справочнике, без добавления в Детализацию
          const newBlockId = await addBlock(name, true);
          const id =
            typeof newBlockId === "string"
              ? parseInt(newBlockId, 10)
              : Number(newBlockId);
          if (Number.isNaN(id)) {
            setNewBlockNameError(
              "Не удалось создать блок. Попробуйте еще раз."
            );
            return;
          }

          // После успешного создания перезагружаем список блоков
          try {
            const data = await getAllBlocks();

            const existingBlockIds = new Set();
            projectData.forEach((block) => {
              if (block.block && block.block.id) {
                existingBlockIds.add(block.block.id);
              }
            });
            localBlocks.forEach((block) => {
              existingBlockIds.add(block.block_id);
            });

            const availableBlocks = data
              .filter(
                (block) => block.is_active && !existingBlockIds.has(block.id)
              )
              .map((block) => ({ value: block.id, label: block.name }));

            setAllBlocksList(data || []);
            setBlocksList(availableBlocks);

            // Сообщаем пользователю, что блок создан, дальше он выбирается из списка
            setSuccessMessage(
              "Блок создан в справочнике. Выберите его из списка и добавьте в Детализацию."
            );
            setShowSuccessPopup(true);
            setTimeout(() => setShowSuccessPopup(false), 3000);
          } catch (reloadErr) {
            console.error(
              "Ошибка при обновлении списка блоков после создания:",
              reloadErr
            );
          }

          // Сбрасываем режим создания, оставляем попап открыт, чтобы пользователь выбрал блок из списка
          setIsCreatingNewBlock(false);
          setNewBlockName("");
          setNewBlockNameError("");
        } catch (err) {
          console.error("Ошибка при создании блока:", err);
          const msg = err?.message || "";
          if (
            /уже существует|already exists|дубликат|duplicate|уникальн|unique/i.test(msg)
          ) {
            setNewBlockNameError("Блок с таким названием уже существует. Выберите его из списка.");
          } else {
            setNewBlockNameError(msg || "Ошибка при создании блока. Попробуйте еще раз.");
          }
        } finally {
          setIsSubmittingAddBlock(false);
        }
        return;
      }

      if (!selectedBlocks || selectedBlocks.length === 0) {
        console.error("Блоки не выбраны");
        return;
      }

      // убираем опцию "создать новый" из выбранных, если вдруг попала
      const blockIdsToAdd = selectedBlocks.filter(
        (v) => v !== CREATE_NEW_BLOCK_OPTION.value
      );
      if (blockIdsToAdd.length === 0) {
        return;
      }

      if (blocksList.length === 0) {
        console.error("Нет доступных блоков для добавления");
        setSuccessMessage("Все доступные блоки уже добавлены в проект");
        setShowSuccessPopup(true);
        setTimeout(() => {
          setShowSuccessPopup(false);
        }, 3000);
        return;
      }

      // Автоматически выбираем первый доступный тип инфраструктуры
      const defaultInfType =
        infoTypes && infoTypes.length > 0 ? infoTypes[0] : null;

      const selectedBlocksCount = blockIdsToAdd.length;
      const startId = nextLocalId;
      const newLocalBlocks = blockIdsToAdd
        .map((selectedBlockValue, index) => {
          const selectedBlockInfo = blocksList.find(
            (b) => b.value === parseInt(selectedBlockValue, 10)
          );

          if (!selectedBlockInfo) {
            console.error("Информация о блоке не найдена");
            return null;
          }

          const localId = startId - index;

          return {
            id: localId,
            floor_count: 1,
            basement_area: 0,
            first_floor_area: 0,
            commercial_area: 0,
            other_area: 0,
            total_area: 0,
            editing_allowed: true,
            inf_type_id: defaultInfType ? defaultInfType.id : null,
            project_id: numericProjectId,
            block_id: parseInt(selectedBlockValue, 10),
            eng_room_ids: [],
            typical_floors: [],
            typical_floors_area: 0,
            typical_floors_count: 0,
            block: {
              id: selectedBlockInfo.value,
              name: selectedBlockInfo.label,
            },
            inf_type: defaultInfType
              ? { id: defaultInfType.id, name: defaultInfType.name }
              : { id: null, name: null },
            is_local: true,
          };
        })
        .filter((block) => block !== null);

      setNextLocalId((prev) => prev - selectedBlocksCount);

      if (newLocalBlocks.length === 0) {
        console.error("Не удалось создать блоки");
        return;
      }

      setLocalBlocks((prev) => [...prev, ...newLocalBlocks]);

      const parkingBlock = newLocalBlocks.find((block) => {
        const blockName = block.block?.name || "";
        return blockName.toLowerCase().includes("паркинг");
      });
      if (parkingBlock) {
        setActiveBlockTab(parkingBlock.id);
      } else {
        setActiveBlockTab(newLocalBlocks[0].id);
      }

      const blockCount = newLocalBlocks.length;
      setSuccessMessage(
        blockCount === 1
          ? "Блок добавлен локально. Заполните данные и нажмите 'Сохранить'"
          : `${blockCount} блоков добавлено локально. Заполните данные и нажмите 'Сохранить'`
      );
      setShowSuccessPopup(true);
      setTimeout(() => {
        setShowSuccessPopup(false);
      }, 3000);

      setShowAddBlockPopup(false);
      setSelectedBlocks([]);
    } catch (error) {
      console.error("Ошибка при добавлении блоков:", error);
    }
  };

  const handleCalcClick = async () => {
    if (!effectiveIsEditable) return; // Не калькулируем если редактирование заблокировано
    try {
      // Получаем данные формы текущего блока
      const calculationData = getCurrentBlockFormData({});

      if (!calculationData) {
        console.error("Данные формы не найдены");
        return;
      }

      // Вызываем API для расчета
      const result = await calculateTotalArea(calculationData);

      // Обновляем типичные этажи с результатами расчета
      if (result.typical_floors && result.typical_floors.length > 0) {
        const updatedTypicalFloors = result.typical_floors.map(
          (floor, index) => {
            // Сохраняем локальные значения, если они есть
            const existingFloors = blockTypicalFloors[activeBlockTab] || [];
            const existingFloor = existingFloors[index];

            return {
              id: Date.now() + index + Math.random(),
              typicalStart: floor.floor_number_from || 0,
              typicalEnd: floor.floor_number_to || 0,
              typicalArea:
                floor.total_area?.toString() || floor.area?.toString() || "0",
              oneFloorArea: floor.area?.toString() || "0",
              // Используем локальное значение количества этажей, если оно есть
              // Это предотвращает перезапись сервером вашего введенного значения
              typicalCount:
                existingFloor?.typicalCount || floor.floors_count || 1,
            };
          }
        );

        setBlockTypicalFloors((prev) => ({
          ...prev,
          [activeBlockTab]: updatedTypicalFloors,
        }));
      }

      // Обновляем общую сумму напрямую из результата расчета
      if (result.total_area !== undefined) {
        setTotal(result.total_area.toString());
        // Сохраняем рассчитанное значение для текущего блока
        setLastCalculatedTotal((prev) => ({
          ...prev,
          [activeBlockTab]: result.total_area,
        }));
      }

      // Обновляем локальный блок в верхней части страницы с результатами калькуляции
      if (
        activeBlockTab &&
        result.floor_count !== undefined &&
        result.total_area !== undefined
      ) {
        setLocalBlocks((prev) =>
          prev.map((block) =>
            block.id === activeBlockTab
              ? {
                  ...block,
                  floor_count: result.floor_count,
                  total_area: result.total_area,
                }
              : block
          )
        );
      }

      // Принудительно обновляем верхнюю таблицу после всех изменений

      // Принудительно обновляем верхнюю таблицу после всех изменений
      setTimeout(() => {
        forceUpdateTopTable();
      }, 0);

      // Не показываем попап для автоматической калькуляции
    } catch (error) {
      console.error("Ошибка при расчете общей площади:", error);
      // Не показываем попап для автоматической калькуляции
    }
  };

  const handleAddTypicalClick = () => {
    if (!effectiveIsEditable) return; // Не добавляем если редактирование заблокировано

    // Вычисляем минимальный номер для типовых этажей на основе нетипичных этажей
    const nonTypicalFloors = formData[activeBlockTab]?.non_typical_floors || [];
    const maxNonTypicalFloor =
      nonTypicalFloors.length > 0
        ? Math.max(
            ...nonTypicalFloors.map(
              (floor) => floor.floorNumber || floor.floor_number || 0
            )
          )
        : 0;
    const minTypicalFloor = maxNonTypicalFloor + 1 || 2;

    setBlockTypicalFloors((prev) => {
      const newTypicalFloor = {
        id: Date.now() + Math.random(),
        typicalStart: minTypicalFloor,
        typicalEnd: minTypicalFloor,
        typicalArea: "0",
        oneFloorArea: "0",
        typicalCount: 1,
      };
      const updated = {
        ...prev,
        [activeBlockTab]: [...(prev[activeBlockTab] || []), newTypicalFloor],
      };
      return updated;
    });
  };

  const handleRemoveTypicalForm = (id, blockId = null) => {
    if (!effectiveIsEditable) return; // Не удаляем если редактирование заблокировано
    const targetBlockId = blockId || activeBlockTab;
    setBlockTypicalFloors((prev) => ({
      ...prev,
      [targetBlockId]: (prev[targetBlockId] || []).filter((f) => f.id !== id),
    }));

    // Принудительно обновляем верхнюю таблицу после удаления типового этажа
    setTimeout(() => {
      forceUpdateTopTable();
    }, 0);
  };

  // Функция калькуляции для типичных этажей
  const handleTypicalFloorsCalculation = async (
    typicalFloorData,
    blockId = null
  ) => {
    try {
      // Используем переданный blockId или активный блок
      const targetBlockId = blockId || activeBlockTab;
      const currentBlockData = allBlocks.find((b) => b.id === targetBlockId);
      if (!currentBlockData) {
        console.error("Текущий блок не найден");
        return;
      }

      // Получаем данные формы текущего блока
      const formDataForBlock = formData[targetBlockId] || {};

      // Проверяем, является ли блок паркингом
      const isParking = isParkingBlock(currentBlockData);

      // Получаем актуальные данные типичных этажей для текущего блока
      const currentTypicalFloors = blockTypicalFloors[targetBlockId] || [];
      const currentTypicalFloor = currentTypicalFloors.find(
        (f) => f.id === typicalFloorData.id
      );

      // Используем обновленные данные из typicalFloorData, если они есть
      const updatedTypicalFloor = {
        typicalStart:
          typicalFloorData.typicalStart !== undefined
            ? typicalFloorData.typicalStart
            : currentTypicalFloor?.typicalStart,
        typicalEnd:
          typicalFloorData.typicalEnd !== undefined
            ? typicalFloorData.typicalEnd
            : currentTypicalFloor?.typicalEnd,
        oneFloorArea:
          typicalFloorData.oneFloorArea !== undefined
            ? typicalFloorData.oneFloorArea
            : currentTypicalFloor?.oneFloorArea,
        // Автоматически рассчитываем количество этажей на основе диапазона
        typicalCount: Math.max(
          0,
          (typicalFloorData.typicalEnd !== undefined
            ? typicalFloorData.typicalEnd
            : currentTypicalFloor?.typicalEnd || 0) -
            (typicalFloorData.typicalStart !== undefined
              ? typicalFloorData.typicalStart
              : currentTypicalFloor?.typicalStart || 0) +
            1
        ).toString(),
      };

      // Подготавливаем данные для калькуляции
      const calculationData = {
        floor_count:
          parseInt(formDataForBlock.floor_count) ||
          currentBlockData.floor_count ||
          0,
        apartments_count: isParking
          ? 0
          : parseInt(formDataForBlock.apartments_count) ||
            currentBlockData.apartments_count ||
            0,
        basement_area: isParking
          ? 0
          : parseFloat(formDataForBlock.basement_area) ||
            currentBlockData.basement_area ||
            0,
        total_area:
          parseFloat(formDataForBlock.total_area) ||
          currentBlockData.total_area ||
          0,
        editing_allowed: currentBlockData.editing_allowed || true,
        inf_type_id:
          parseInt(formDataForBlock.inf_type_id) ||
          currentBlockData.inf_type?.id ||
          0,
        project_id: parseInt(numericProjectId),
        block_id: parseInt(currentBlockData.block?.id || 0),
        eng_room_ids:
          formDataForBlock.eng_room_ids ||
          currentBlockData.eng_rooms?.map((room) => room.id) ||
          [],
        typical_floors: [
          {
            floor_number_from: parseInt(updatedTypicalFloor.typicalStart) || 0,
            floor_number_to: parseInt(updatedTypicalFloor.typicalEnd) || 0,
            area: parseFloat(updatedTypicalFloor.oneFloorArea) || 0,
          },
        ],
        non_typical_floors: (formDataForBlock.non_typical_floors || []).map(
          (floor) => {
            const commercialArea = parseFloat(floor.commercialArea) || 0;
            const otherArea = parseFloat(floor.otherArea) || 0;
            const floorNumber = parseInt(floor.floorNumber) || 0;
            // Передаем totalArea для всех этажей, если оно введено пользователем
            const totalAreaValue =
              parseFloat(floor.totalArea || floor.total_area || "0") || 0;
            // Вычисляем total_area как сумму коммерческой и остальной площади,
            // если totalArea не введена или равна 0
            const calculatedTotalArea =
              totalAreaValue > 0 ? totalAreaValue : commercialArea + otherArea;
            return {
              commercial_area: commercialArea,
              other_area: otherArea,
              floor_number: floorNumber,
              total_area: calculatedTotalArea,
            };
          }
        ),
      };

      // Вызываем API для расчета
      const result = await calculateTotalArea(calculationData);

      // Обновляем данные типичных этажей с результатами расчета
      if (result.typical_floors && result.typical_floors.length > 0) {
        const calculatedFloor = result.typical_floors[0];

        // Проверяем, что сервер вернул новые данные
        const newTotalArea = calculatedFloor.total_area?.toString() || "0";

        // Используем расчетное количество этажей на основе диапазона
        const calculatedFloorsCount = Math.max(
          0,
          parseInt(updatedTypicalFloor.typicalEnd || 0) -
            parseInt(updatedTypicalFloor.typicalStart || 0) +
            1
        ).toString();

        // Вычисляем локальную общую площадь: площадь одного этажа * количество этажей
        const oneFloorAreaValue = parseNumericValue(
          updatedTypicalFloor.oneFloorArea || "0"
        );
        const typicalCountValue = parseInt(calculatedFloorsCount) || 0;
        const localTotalArea = oneFloorAreaValue * typicalCountValue;

        // Обновляем значения в форме типичных этажей, сохраняя обновленные данные
        const finalUpdatedTypicalFloor = {
          ...currentTypicalFloor,
          ...updatedTypicalFloor, // Сохраняем обновленные значения
          typicalArea: smartRound(localTotalArea).toString(), // Используем локальное вычисление
          typicalCount: calculatedFloorsCount, // Используем расчетное количество
        };

        // Обновляем состояние типичных этажей для правильного блока
        setBlockTypicalFloors((prev) => {
          const newState = {
            ...prev,
            [targetBlockId]: (prev[targetBlockId] || []).map((floor) =>
              floor.id === typicalFloorData.id
                ? finalUpdatedTypicalFloor
                : floor
            ),
          };
          return newState;
        });

        // Принудительно обновляем верхнюю таблицу после изменения типовых этажей
        setTimeout(() => {
          forceUpdateTopTable();
        }, 0);

        // Принудительно обновляем общую сумму
        if (result.total_area !== undefined) {
          setTotal(result.total_area.toString());
          setLastCalculatedTotal((prev) => ({
            ...prev,
            [activeBlockTab]: result.total_area,
          }));
        }

        // Обновляем formData с новыми данными типичных этажей из результата калькуляции
        setFormData((prev) => {
          const currentFormData = prev[activeBlockTab] || {};

          // Используем данные из результата калькуляции, а не из blockTypicalFloors
          const updatedTypicalFloors =
            result.typical_floors?.map((floor) => ({
              floor_number_from: parseInt(floor.floor_number_from) || 0,
              floor_number_to: parseInt(floor.floor_number_to) || 0,
              area: parseFloat(floor.area) || 0,
              floors_count: parseInt(floor.floors_count) || 0,
              total_area: parseFloat(floor.total_area) || 0,
            })) || [];

          const newFormData = {
            ...prev,
            [activeBlockTab]: {
              ...currentFormData,
              typical_floors: updatedTypicalFloors,
              typical_floors_area: result.typical_floors_area || 0,
              typical_floors_count: result.typical_floors_count || 0,
            },
          };

          return newFormData;
        });
      }

      // Обновляем общую сумму
      if (result.total_area !== undefined) {
        setTotal(result.total_area.toString());
        setLastCalculatedTotal((prev) => ({
          ...prev,
          [activeBlockTab]: result.total_area,
        }));
      }

      // Обновляем локальный блок в верхней части страницы с результатами калькуляции
      if (
        activeBlockTab &&
        result.floor_count !== undefined &&
        result.total_area !== undefined
      ) {
        setLocalBlocks((prev) => {
          const currentBlock = prev.find(
            (block) => block.id === activeBlockTab
          );
          if (!currentBlock) return prev;

          // Проверяем, действительно ли данные изменились
          const hasFloorCountChanged =
            result.floor_count !== currentBlock.floor_count;
          const hasTotalAreaChanged =
            result.total_area !== currentBlock.total_area;

          if (!hasFloorCountChanged && !hasTotalAreaChanged) {
            return prev; // Не обновляем, если данные не изменились
          }

          return prev.map((block) =>
            block.id === activeBlockTab
              ? {
                  ...block,
                  floor_count: result.floor_count,
                  total_area: result.total_area,
                }
              : block
          );
        });
      }

      // Принудительно обновляем верхнюю таблицу после всех изменений

      // Принудительно обновляем верхнюю таблицу после всех изменений
      setTimeout(() => {
        forceUpdateTopTable();
      }, 0);

      // Не показываем попап для автоматической калькуляции типичных этажей
    } catch (error) {
      console.error("Ошибка при калькуляции типичных этажей:", error);
      // Не показываем попап для автоматической калькуляции типичных этажей
    }
  };

  // Функция для сбора данных из формы текущего блока для расчета
  const getCurrentBlockFormData = (overrides = {}) => {
    const currentBlockData = allBlocks.find((b) => b.id === activeBlockTab);
    if (!currentBlockData) return null;

    // Проверяем, является ли блок паркингом
    const isParking = isParkingBlock(currentBlockData);

    // Получаем данные из формы (если они есть)
    const formData = getFormDataForBlock(activeBlockTab);

    // Получаем актуальные данные нетипичных этажей из состояния
    // Сначала пробуем из formData, если нет - из blockData
    let nonTypicalFloorsData = formData?.non_typical_floors || [];

    // Если в formData нет данных нетипичных этажей, но они есть в blockData
    // И только если нет переданных значений (overrides)
    if (
      nonTypicalFloorsData.length === 0 &&
      currentBlockData.non_typical_floors &&
      Object.keys(overrides).length === 0
    ) {
      nonTypicalFloorsData = currentBlockData.non_typical_floors.map(
        (floor) => ({
          commercialArea: floor.commercial_area?.toString() || "0",
          otherArea: floor.other_area?.toString() || "0",
          floorNumber: floor.floor_number || 0,
          totalArea: floor.total_area?.toString() || "0",
        })
      );
    }

    const result = {
      floor_count: parseInt(
        overrides.floor_count ||
          formData?.floor_count ||
          currentBlockData.floor_count ||
          0
      ),
      apartments_count: isParking
        ? 0
        : parseInt(
            overrides.apartments_count ||
              formData?.apartments_count ||
              currentBlockData.apartments_count ||
              0
          ),
      basement_area: isParking
        ? 0
        : parseFloat(
            overrides.basement_area ||
              formData?.basement_area ||
              currentBlockData.basement_area ||
              0
          ),
      total_area: parseFloat(
        lastCalculatedTotal[activeBlockTab] || currentBlockData.total_area || 0
      ),
      editing_allowed: currentBlockData.editing_allowed || true,
      inf_type_id: parseInt(
        overrides.inf_type_id ||
          formData?.inf_type_id ||
          currentBlockData.inf_type?.id ||
          0
      ),
      project_id: parseInt(numericProjectId),
      block_id: parseInt(currentBlockData.block?.id || 0),
      eng_room_ids:
        overrides.eng_room_ids ||
        formData?.eng_room_ids ||
        currentBlockData.eng_rooms?.map((room) => room.id) ||
        [],
      typical_floors:
        blockTypicalFloors[activeBlockTab]?.map((floor) => ({
          floor_number_from: floor.typicalStart,
          floor_number_to: floor.typicalEnd,
          area: parseFloat(floor.oneFloorArea) || 0,
        })) || [],
      non_typical_floors: (
        overrides.non_typical_floors || nonTypicalFloorsData
      ).map((floor) => {
        const commercialArea = parseNumericValue(
          (floor.commercial_area || floor.commercialArea || "0").toString()
        );
        const otherArea = parseNumericValue(
          (floor.other_area || floor.otherArea || "0").toString()
        );
        const floorNumber =
          parseInt(floor.floor_number || floor.floorNumber) || 0;
        // Передаем totalArea для всех этажей, если оно введено пользователем
        // Если не введено, вычисляем как commercial_area + other_area
        const totalAreaValue = parseNumericValue(
          (floor.total_area || floor.totalArea || "0").toString()
        );
        // Вычисляем total_area как сумму коммерческой и остальной площади,
        // если totalArea не введена или равна 0
        const calculatedTotalArea =
          totalAreaValue > 0 ? totalAreaValue : commercialArea + otherArea;
        return {
          commercial_area: commercialArea,
          other_area: otherArea,
          floor_number: floorNumber,
          total_area: calculatedTotalArea,
        };
      }),
    };

    // Проверяем формат данных нетипичных этажей

    return result;
  };

  // Функция для получения данных формы для конкретного блока
  const getFormDataForBlock = (blockId) => {
    return formData[blockId] || null;
  };

  // Функция для валидации данных перед сохранением
  const validateBlockData = (data) => {
    const errors = [];

    // Проверяем обязательные поля
    if (!data.project_id || data.project_id <= 0) {
      errors.push("ID проекта должен быть больше 0");
    }

    if (!data.block_id || data.block_id <= 0) {
      errors.push("ID блока должен быть больше 0");
    }

    if (!data.inf_type_id || data.inf_type_id <= 0) {
      errors.push("Тип инфраструктуры должен быть выбран");
    }

    // Проверяем числовые значения
    if (data.floor_count < 0) {
      errors.push("Количество этажей не может быть отрицательным");
    }

    if (data.apartments_count < 0) {
      errors.push("Количество квартир не может быть отрицательным");
    }

    if (data.basement_area < 0) {
      errors.push("Площадь подвала не может быть отрицательной");
    }

    if (data.total_area < 0) {
      errors.push("Общая площадь не может быть отрицательной");
    }

    // Проверяем нетипичные этажи
    if (data.non_typical_floors && data.non_typical_floors.length > 0) {
      data.non_typical_floors.forEach((floor, index) => {
        if (floor.floor_number <= 0) {
          errors.push(
            `Нетипичный этаж ${index + 1}: номер этажа должен быть больше 0`
          );
        }
        if (floor.commercial_area < 0) {
          errors.push(
            `Нетипичный этаж ${
              index + 1
            }: коммерческая площадь не может быть отрицательной`
          );
        }
        if (floor.other_area < 0) {
          errors.push(
            `Нетипичный этаж ${
              index + 1
            }: прочая площадь не может быть отрицательной`
          );
        }
        if (floor.total_area < 0) {
          errors.push(
            `Нетипичный этаж ${
              index + 1
            }: общая площадь не может быть отрицательной`
          );
        }
      });
    }

    // Проверяем типичные этажи
    if (data.typical_floors && data.typical_floors.length > 0) {
      data.typical_floors.forEach((floor, index) => {
        if (floor.floor_number_from <= 0) {
          errors.push(
            `Типичный этаж ${
              index + 1
            }: начальный номер этажа должен быть больше 0`
          );
        }
        if (floor.floor_number_to <= 0) {
          errors.push(
            `Типичный этаж ${
              index + 1
            }: конечный номер этажа должен быть больше 0`
          );
        }
        if (floor.floor_number_from > floor.floor_number_to) {
          errors.push(
            `Типичный этаж ${
              index + 1
            }: начальный номер этажа не может быть больше конечного`
          );
        }
        if (floor.area < 0) {
          errors.push(
            `Типичный этаж ${index + 1}: площадь не может быть отрицательной`
          );
        }
        if (floor.total_area < 0) {
          errors.push(
            `Типичный этаж ${
              index + 1
            }: общая площадь не может быть отрицательной`
          );
        }
      });
    }

    return errors;
  };

  // Функция для сбора данных из формы текущего блока для сохранения
  // Функция для получения данных сохранения конкретного блока
  const getBlockSaveData = (blockId) => {
    const currentBlockData = allBlocks.find((b) => b.id === blockId);
    if (!currentBlockData) return null;

    // Проверяем, является ли блок паркингом
    const isParking = isParkingBlock(currentBlockData);

    // Получаем данные из формы (если они есть)
    const formData = getFormDataForBlock(blockId);

    const resolvedBlockId = parseInt(
      currentBlockData.block?.id || currentBlockData.block_id || 0
    );

    const saveData = {
      floor_count: parseInt(
        formData?.floor_count !== undefined
          ? formData.floor_count
          : currentBlockData.floor_count || 0
      ),
      apartments_count: isParking
        ? 0
        : parseInt(
            formData?.apartments_count !== undefined
              ? formData.apartments_count
              : currentBlockData.apartments_count || 0
          ),
      basement_area: isParking
        ? 0
        : parseFloat(
            formData?.basement_area !== undefined
              ? formData.basement_area
              : currentBlockData.basement_area || 0
          ),
      total_area: isParking
        ? parseFloat(
            formData?.total_area !== undefined
              ? formData.total_area
              : lastCalculatedTotal[blockId] || currentBlockData.total_area || 0
          )
        : parseFloat(
            lastCalculatedTotal[blockId] || currentBlockData.total_area || 0
          ),
      editing_allowed: currentBlockData.editing_allowed || true,
      inf_type_id: parseInt(
        formData?.inf_type_id !== undefined
          ? formData.inf_type_id
          : currentBlockData.inf_type?.id || 0
      ),
      project_id: parseInt(numericProjectId),
      block_id: resolvedBlockId,
      eng_room_ids:
        formData?.eng_room_ids ||
        currentBlockData.eng_rooms?.map((room) => room.id) ||
        [],
      typical_floors:
        blockTypicalFloors[blockId]?.map((floor) => {
          const typicalFloor = {
            floor_number_from: parseInt(floor.typicalStart) || 0,
            floor_number_to: parseInt(floor.typicalEnd) || 0,
            area: parseFloat(floor.oneFloorArea) || 0,
            floors_count: parseInt(floor.typicalCount) || 1,
            total_area: parseFloat(floor.typicalArea) || 0,
          };

          return typicalFloor;
        }) || [],
      typical_floors_area: parseFloat(
        (blockTypicalFloors[blockId] || []).reduce((total, floor) => {
          return total + (parseFloat(floor.typicalArea) || 0);
        }, 0) ||
          currentBlockData.typical_floors_area ||
          0
      ),
      typical_floors_count: (() => {
        const calculatedCount = parseInt(
          (blockTypicalFloors[blockId] || []).reduce((total, floor) => {
            return total + (parseInt(floor.typicalCount) || 0);
          }, 0) ||
            currentBlockData.typical_floors_count ||
            0
        );
        return calculatedCount;
      })(),
      non_typical_floors: (formData?.non_typical_floors || []).map((floor) => {
        // Создаем объект без поля id, так как сервер может его не ожидать
        const commercialArea = parseNumericValue(
          (floor.commercialArea || "0").toString()
        );
        const otherArea = parseNumericValue(
          (floor.otherArea || "0").toString()
        );
        const floorNumber = parseInt(floor.floorNumber) || 0;
        const totalAreaValue = parseNumericValue(
          (floor.totalArea || "0").toString()
        );
        // Вычисляем total_area как сумму коммерческой и остальной площади,
        // если totalArea не введена или равна 0
        const calculatedTotalArea =
          totalAreaValue > 0 ? totalAreaValue : commercialArea + otherArea;
        const floorData = {
          commercial_area: commercialArea,
          other_area: otherArea,
          floor_number: floorNumber,
          total_area: calculatedTotalArea,
        };

        // Удаляем поле id, если оно есть

        return floorData;
      }),
    };

    // Валидируем данные перед возвратом
    const validationErrors = validateBlockData(saveData);
    if (validationErrors.length > 0) {
      console.error("Ошибки валидации данных:", validationErrors);
      throw new Error(
        "Ошибки валидации данных:\n" + validationErrors.join("\n")
      );
    }

    return saveData;
  };

  // Функция для получения данных сохранения текущего активного блока
  const getCurrentBlockSaveData = () => {
    return getBlockSaveData(activeBlockTab);
  };

  // Функция автоматической калькуляции при изменении полей
  const handleAutoCalc = async (updatedValues = {}) => {
    try {
      // Получаем данные формы текущего блока
      const calculationData = getCurrentBlockFormData(updatedValues);

      if (!calculationData) {
        console.error("Данные формы не найдены для автоматической калькуляции");
        return;
      }

      // Обновляем данные с переданными значениями
      const updatedCalculationData = {
        ...calculationData,
        ...updatedValues,
      };

      // Вызываем API для расчета
      const result = await calculateTotalArea(updatedCalculationData);

      // Обновляем только общую сумму из результата расчета
      if (result.total_area !== undefined) {
        setTotal(result.total_area.toString());
        // Сохраняем рассчитанное значение для текущего блока
        setLastCalculatedTotal((prev) => ({
          ...prev,
          [activeBlockTab]: result.total_area,
        }));
      }

      // При автоматической калькуляции НЕ обновляем данные блока, чтобы сохранить пользовательский ввод
      // Обновляем только общую площадь в состоянии
      // Для паркинга не обновляем lastCalculatedTotal, так как автоматическая калькуляция возвращает 0
      const currentBlockDataForCalc = allBlocks.find(
        (b) => b.id === activeBlockTab
      );
      const isParkingForCalc = currentBlockDataForCalc
        ? isParkingBlock(currentBlockDataForCalc)
        : false;

      if (result.total_area !== undefined) {
        setTotal(result.total_area.toString());
        // Для паркинга не обновляем lastCalculatedTotal, используем значение из formData
        if (!isParkingForCalc) {
          setLastCalculatedTotal((prev) => ({
            ...prev,
            [activeBlockTab]: result.total_area,
          }));
        }
      }

      // Обновляем локальный блок в верхней части страницы с результатами калькуляции
      if (
        activeBlockTab &&
        result.floor_count !== undefined &&
        result.total_area !== undefined
      ) {
        setLocalBlocks((prev) =>
          prev.map((block) =>
            block.id === activeBlockTab
              ? {
                  ...block,
                  floor_count: result.floor_count,
                  total_area: result.total_area,
                }
              : block
          )
        );
      }

      // Принудительно обновляем верхнюю таблицу после всех изменений

      // Принудительно обновляем верхнюю таблицу после всех изменений
      setTimeout(() => {
        forceUpdateTopTable();
      }, 0);
    } catch (error) {
      console.error("Ошибка при автоматической калькуляции:", error);
      // Не показываем попап для автоматической калькуляции
    }
  };

  // Функция для обработки изменений данных формы
  const handleFormDataChange = React.useCallback(
    (blockId, data) => {
      // Если изменился тип секции, автоматически устанавливаем коэффициент
      if (data.inf_type_id !== undefined) {
        setCoefficientBySectionType(blockId, data.inf_type_id);
      }

      setFormData((prev) => {
        const prevBlockData = prev[blockId];
        const prevDataStr = prevBlockData
          ? JSON.stringify(prevBlockData)
          : null;
        const newBlockData = {
          ...prevBlockData,
          ...data,
        };
        const newDataStr = JSON.stringify(newBlockData);

        // Если данные не изменились, не обновляем состояние
        if (prevDataStr === newDataStr) {
          return prev;
        }

        return {
          ...prev,
          [blockId]: newBlockData,
        };
      });

      // Обновляем локальный блок в верхней части страницы с данными из формы
      if (
        blockId &&
        (data.floor_count !== undefined || data.total_area !== undefined)
      ) {
        setLocalBlocks((prev) => {
          const currentBlock = prev.find((block) => block.id === blockId);
          if (!currentBlock) return prev;

          // Проверяем, действительно ли данные изменились
          const hasFloorCountChanged =
            data.floor_count !== undefined &&
            data.floor_count !== currentBlock.floor_count;
          const hasTotalAreaChanged =
            data.total_area !== undefined &&
            data.total_area !== currentBlock.total_area;

          if (!hasFloorCountChanged && !hasTotalAreaChanged) {
            return prev; // Не обновляем, если данные не изменились
          }

          return prev.map((block) =>
            block.id === blockId
              ? {
                  ...block,
                  floor_count:
                    data.floor_count !== undefined
                      ? data.floor_count
                      : block.floor_count,
                  total_area:
                    data.total_area !== undefined
                      ? data.total_area
                      : block.total_area,
                }
              : block
          );
        });
      }

      // Если изменились данные площади, также обновляем total_area в localBlocks

      // Если изменились данные площади, также обновляем total_area в localBlocks

      // Если изменились данные площади, также обновляем total_area в localBlocks
      if (
        blockId &&
        (data.basement_area !== undefined ||
          data.first_floor_area !== undefined ||
          data.commercial_area !== undefined ||
          data.other_area !== undefined)
      ) {
        // НЕ обновляем localBlocks здесь, чтобы не сбрасывать инпуты
        // Площадь будет обновляться только при калькуляции или сохранении
      }

      // Принудительно обновляем верхнюю таблицу после изменения данных формы
      setTimeout(() => {
        forceUpdateTopTable();
      }, 0);
    },
    [setCoefficientBySectionType]
  );

  // Функция для сохранения настроек проекта (переключатели, площадь участка, схема)
  const handleSaveProjectSettings = async () => {
    if (!effectiveIsEditable) return; // Не сохраняем если редактирование заблокировано
    try {
      // Подготавливаем данные для сохранения
      const blocksData = allBlocks.map((block) => {
        // Получаем актуальную площадь блока из разных источников
        // Используем только результат от API (lastCalculatedTotal) или данные с бэкенда
        // Вся калькуляция должна выполняться на бэкенде через API calculateTotalArea
        let actualTotalArea = block.total_area || 0;

        // Если есть рассчитанная площадь для этого блока от API, используем её
        if (lastCalculatedTotal[block.id] !== undefined) {
          actualTotalArea = lastCalculatedTotal[block.id];
        } else {
          // Иначе используем данные из формы или блока
          const formDataForBlock = formData[block.id];
          if (formDataForBlock?.total_area !== undefined) {
            actualTotalArea = parseNumericValue(
              formDataForBlock.total_area.toString()
            );
          }
        }

        const blockData = {
          block_id: parseInt(block.block?.id || 0),
          block_name: block.block?.name || block.name || "Без названия",
          floor_count: parseInt(block.floor_count || 0),
          total_area: parseFloat(actualTotalArea || 0),
          coefficient: parseFloat(
            blockExtras[block.id]?.coefficient !== null &&
              blockExtras[block.id]?.coefficient !== undefined
              ? blockExtras[block.id]?.coefficient
              : 0
          ),
          price_per_m2: parseFloat(
            blockExtras[block.id]?.rate !== null &&
              blockExtras[block.id]?.rate !== undefined
              ? blockExtras[block.id]?.rate
              : 0
          ),
        };

        return blockData;
      });

      const saveData = {
        blocks: blocksData, // Может быть пустым массивом, если блоков нет
        hec_land_area: parseFloat(plotAreaHa) || 0,
        apply_floor_coefficient: applyFloorCoefficient,
        apply_acceleration_coefficient: applyAccelerationCoefficient,
        apply_bim_surcharge_coefficient: applyBimCoefficient,
        smeta_documentation_percent: parseFloat(smetaDocumentationPercent) || 0,
        blocks_schema_image_url: blocksSchemaImageUrl || "",
      };

      // Вызываем API для сохранения настроек
      await saveProjectBlocks(numericProjectId, saveData);

      setSuccessMessage("Настройки проекта успешно сохранены");
      setShowSuccessPopup(true);
      setTimeout(() => {
        setShowSuccessPopup(false);
      }, 3000);
    } catch (error) {
      console.error("Ошибка при сохранении настроек проекта:", error);
      setSuccessMessage(`Ошибка при сохранении: ${error.message}`);
      setShowSuccessPopup(true);
      setTimeout(() => {
        setShowSuccessPopup(false);
      }, 3000);
    }
  };

  // Функция для сохранения изменений в блоке
  // Функция для сохранения всех локальных блоков последовательно
  const handleSaveAllBlocks = async () => {
    if (!effectiveIsEditable) return; // Не сохраняем если редактирование заблокировано

    if (localBlocks.length === 0) {
      setSuccessMessage("Нет блоков для сохранения");
      setShowSuccessPopup(true);
      setTimeout(() => setShowSuccessPopup(false), 3000);
      return;
    }

    // В запросы должен уходить только ID проекта из роута (положительное число).
    // Локальные ID блоков отрицательные — их нельзя подставлять в getCpTeis.
    const projectIdForApi =
      typeof projectId !== "undefined" &&
      projectId !== null &&
      projectId !== "" &&
      Number.isInteger(numericProjectId) &&
      numericProjectId > 0
        ? numericProjectId
        : null;

    if (!projectIdForApi) {
      console.warn(
        "Сохранение блоков: неверный projectId из роута, синхронизация после сохранения будет пропущена.",
        { projectId, numericProjectId }
      );
    }

    setIsSavingAllBlocks(true); // Включаем состояние загрузки

    try {
      // Создаем копию массива локальных блоков для итерации
      const blocksToSave = [...localBlocks];
      const totalBlocks = blocksToSave.length;
      let successCount = 0;
      let errorCount = 0;
      const errors = [];
      const savedBlocksFromApi = [];

      // Сохраняем каждый блок последовательно
      for (let i = 0; i < blocksToSave.length; i++) {
        const block = blocksToSave[i];
        try {
          // Получаем данные для сохранения
          const saveData = getBlockSaveData(block.id);

          if (!saveData) {
            throw new Error("Не удалось получить данные для сохранения");
          }

          // Сохраняем блок
          const result = await addCpTei(saveData);

          if (result?.data?.id) {
            savedBlocksFromApi.push(result.data);

            // Удаляем блок из локального состояния
            setLocalBlocks((prev) => prev.filter((b) => b.id !== block.id));

            // Обновляем blockExtras для нового ID блока
            setBlockExtras((prev) => ({
              ...prev,
              [result.data.id]: {
                coefficient: blockExtras[block.id]?.coefficient || 0,
                rate: blockExtras[block.id]?.rate || 0,
              },
            }));

            // Удаляем старые данные из blockExtras
            setBlockExtras((prev) => {
              const { [block.id]: removed, ...rest } = prev;
              return rest;
            });

            successCount++;
          } else {
            throw new Error("ID блока не получен от сервера");
          }
        } catch (error) {
          errorCount++;
          const errorMessage = `Ошибка при сохранении блока "${
            block.block?.name || block.id
          }": ${error.message}`;
          errors.push(errorMessage);
          console.error(errorMessage, error);
        }
      }

      // Сразу обновляем UI сохранёнными блоками из ответов API, чтобы не зависеть от getCpTeis
      // (бэкенд часто отдаёт 500 на GET сразу после записи — после перезагрузки данные есть).
      if (savedBlocksFromApi.length > 0) {
        setProjectData((prev) => {
          const next = [...prev, ...savedBlocksFromApi];
          processTypicalFloors(next, true);
          return next;
        });
      }

      // Пытаемся подтянуть полные данные с сервера (если GET ещё отдаёт 500 — UI уже обновлён выше).
      if (projectIdForApi) {
        const fetchFreshWithRetry = async (retries = 3, delayMs = 400) => {
          for (let attempt = 0; attempt < retries; attempt++) {
            if (attempt > 0) {
              await new Promise((r) => setTimeout(r, delayMs * attempt));
            }
            try {
              return await getCpTeis(projectIdForApi);
            } catch (err) {
              if (attempt === retries - 1) throw err;
            }
          }
        };
        try {
          const freshProjectData = await fetchFreshWithRetry();
          const freshBlocks = freshProjectData.blocks || [];
          setProjectData(freshBlocks);
          processTypicalFloors(freshBlocks, true);
        } catch (syncError) {
          console.warn(
            "Не удалось синхронизировать данные проекта (после сохранения блоки уже отображаются):",
            syncError
          );
        }
      }

      // Автоматически сохраняем настройки проекта
      try {
        await handleSaveProjectSettings();
      } catch (settingsError) {
        console.error(
          "Ошибка при автоматическом сохранении настроек:",
          settingsError
        );
      }

      // Показываем результат
      if (errorCount === 0) {
        setSuccessMessage(`Все блоки (${successCount}) успешно сохранены`);
      } else {
        setSuccessMessage(
          `Сохранено блоков: ${successCount} из ${totalBlocks}. Ошибок: ${errorCount}`
        );
        console.error("Ошибки при сохранении:", errors);
      }

      // После успешного сохранения скрываем детали до следующего выбора
      setActiveBlockTab(null);

      setShowSuccessPopup(true);
      setTimeout(() => setShowSuccessPopup(false), 5000);
    } catch (error) {
      console.error("Ошибка при сохранении блоков:", error);
      setSuccessMessage(`Ошибка при сохранении блоков: ${error.message}`);
      setShowSuccessPopup(true);
      setTimeout(() => setShowSuccessPopup(false), 5000);
    } finally {
      setIsSavingAllBlocks(false); // Выключаем состояние загрузки
    }
  };

  const handleSaveBlock = async () => {
    if (!effectiveIsEditable) return; // Не сохраняем если редактирование заблокировано
    try {
      // Проверяем, является ли текущий блок локальным
      const currentLocalBlock = localBlocks.find(
        (b) => b.id === activeBlockTab
      );
      const currentBlockData = projectData.find((b) => b.id === activeBlockTab);

      // Если currentBlockData не найден, но activeBlockTab установлен,
      // возможно данные еще не синхронизированы
      if (!currentBlockData && activeBlockTab && !currentLocalBlock) {
        console.warn(
          "Блок не найден в данных, возможно требуется синхронизация"
        );
        // Попробуем найти блок по другим критериям
        const alternativeBlock = projectData.find(
          (b) =>
            b.id === activeBlockTab ||
            b.temp_id === activeBlockTab ||
            b.local_id === activeBlockTab
        );
        if (alternativeBlock) {
        }
      }

      if (!currentLocalBlock && !currentBlockData) {
        console.error("Текущий блок не найден");
        return;
      }

      // Получаем данные для сохранения
      const saveData = getCurrentBlockSaveData();

      if (!saveData) {
        console.error("Данные для сохранения не найдены");
        return;
      }

      // Создаем упрощенную версию данных для тестирования
      const simplifiedData = {
        floor_count: saveData.floor_count,
        apartments_count: saveData.apartments_count,
        basement_area: saveData.basement_area,
        total_area: saveData.total_area,
        editing_allowed: saveData.editing_allowed,
        inf_type_id: saveData.inf_type_id,
        project_id: saveData.project_id,
        block_id: saveData.block_id,
        eng_room_ids: saveData.eng_room_ids,
        typical_floors: saveData.typical_floors,
        typical_floors_area: saveData.typical_floors_area,
        typical_floors_count: saveData.typical_floors_count,
        non_typical_floors: saveData.non_typical_floors,
      };

      // Валидация данных перед отправкой
      const requiredFields = [
        "floor_count",
        "apartments_count",
        "basement_area",
        "total_area",
        "inf_type_id",
        "project_id",
        "block_id",
        "eng_room_ids",
        "typical_floors_area",
        "typical_floors_count",
      ];

      const missingFields = requiredFields.filter((field) => {
        const value = simplifiedData[field];
        return (
          value === undefined ||
          value === null ||
          (Array.isArray(value) &&
            value.length === 0 &&
            field !== "eng_room_ids")
        );
      });

      if (missingFields.length > 0) {
        console.error("Отсутствуют обязательные поля:", missingFields);
        throw new Error(
          `Отсутствуют обязательные поля: ${missingFields.join(", ")}`
        );
      }

      // Проверяем корректность данных типичных этажей
      if (simplifiedData.typical_floors.length > 0) {
        const invalidTypicalFloors = simplifiedData.typical_floors.filter(
          (floor) => {
            const isValid =
              floor.floor_number_from !== undefined &&
              floor.floor_number_to !== undefined &&
              floor.area !== undefined &&
              floor.floors_count !== undefined &&
              floor.total_area !== undefined;

            if (!isValid) {
              console.warn("Некорректный типовой этаж:", floor);
            }

            return !isValid;
          }
        );

        if (invalidTypicalFloors.length > 0) {
          console.error(
            "Некорректные данные типичных этажей:",
            invalidTypicalFloors
          );
          throw new Error("Некорректные данные типичных этажей");
        }
      } else {
        console.warn("Типовые этажи отсутствуют в данных для сохранения!");
      }

      let result;

      if (currentLocalBlock) {
        // Если это локальный блок, добавляем его в базу
        result = await addCpTei(saveData);

        // Удаляем блок из локального состояния
        setLocalBlocks((prev) =>
          prev.filter((b) => b.id !== currentLocalBlock.id)
        );

        // Теперь у нас есть ID блока из ответа сервера!
        if (result?.data?.id) {
          // Обновляем activeBlockTab на реальный ID
          setActiveBlockTab(result.data.id);

          // Обновляем blockExtras для нового ID блока
          setBlockExtras((prev) => ({
            ...prev,
            [result.data.id]: {
              coefficient: blockExtras[currentLocalBlock.id]?.coefficient || 0,
              rate: blockExtras[currentLocalBlock.id]?.rate || 0,
            },
          }));

          // Удаляем старые данные из blockExtras
          setBlockExtras((prev) => {
            const { [currentLocalBlock.id]: removed, ...rest } = prev;
            return rest;
          });

          // Проверяем, есть ли типовые этажи в созданном блоке
          if (
            !result.data.typical_floors ||
            result.data.typical_floors.length === 0
          ) {
            console.warn("В созданном блоке отсутствуют типовые этажи!");

            // Попробуем обновить блок, добавив типовые этажи
            try {
              // Создаем корректные данные для обновления
              const updateData = {
                floor_count: parseInt(
                  result.data.floor_count || saveData.floor_count || 0
                ),
                apartments_count: parseInt(
                  result.data.apartments_count || saveData.apartments_count || 0
                ),
                basement_area: parseFloat(
                  result.data.basement_area || saveData.basement_area || 0
                ),
                total_area: parseFloat(
                  result.data.total_area || saveData.total_area || 0
                ),
                editing_allowed: true,
                inf_type_id: parseInt(
                  result.data.inf_type?.id || saveData.inf_type_id || 0
                ),
                eng_room_ids:
                  result.data.eng_rooms?.map((r) => r.id) ||
                  saveData.eng_room_ids ||
                  [],
                typical_floors: (saveData.typical_floors || []).map(
                  (floor) => ({
                    floor_number_from: parseInt(floor.floor_number_from) || 0,
                    floor_number_to: parseInt(floor.floor_number_to) || 0,
                    area: parseFloat(floor.area) || 0,
                    floors_count: parseInt(floor.floors_count) || 1,
                    total_area: parseFloat(floor.total_area) || 0,
                  })
                ),
                typical_floors_area: parseFloat(
                  saveData.typical_floors_area || 0
                ),
                typical_floors_count: parseInt(
                  saveData.typical_floors_count || 0
                ),
                non_typical_floors: (
                  result.data.non_typical_floors ||
                  saveData.non_typical_floors ||
                  []
                ).map((floor) => ({
                  commercial_area: parseFloat(floor.commercial_area) || 0,
                  other_area: parseFloat(floor.other_area) || 0,
                  floor_number: parseInt(floor.floor_number) || 0,
                  total_area: parseFloat(floor.total_area) || 0,
                })),
                // Сервер требует эти поля даже при обновлении
                project_id: parseInt(
                  result.data.project?.id || saveData.project_id || 0
                ),
                block_id: parseInt(
                  result.data.block?.id || saveData.block_id || 0
                ),
              };
            } catch (updateError) {
              console.error(
                "Ошибка при обновлении блока с типовыми этажами:",
                updateError
              );
            }
          }

          // Перезагружаем данные проекта для синхронизации
          try {
            const freshProjectData = await getCpTeis(numericProjectId);
            const freshBlocks = freshProjectData.blocks || [];
            setProjectData(freshBlocks);

            // Обрабатываем типичные этажи для обновленных данных
            processTypicalFloors(freshBlocks, true);
          } catch (syncError) {
            console.warn(
              "Не удалось синхронизировать данные проекта:",
              syncError
            );
          }

          // Автоматически сохраняем настройки проекта после успешного создания блока
          try {
            await handleSaveProjectSettings();
          } catch (settingsError) {
            console.error(
              "Ошибка при автоматическом сохранении настроек:",
              settingsError
            );
          }

          setSuccessMessage("Блок и настройки проекта успешно сохранены");
          setShowSuccessPopup(true);
          setTimeout(() => setShowSuccessPopup(false), 3000);
          return; // Выходим из функции, так как блок уже обработан
        } else {
          console.warn(
            "ID блока не найден в ответе сервера, используем резервную логику"
          );

          // Принудительно перезагружаем данные проекта для получения реального ID
          // Небольшая задержка для стабилизации данных на сервере
          await new Promise((resolve) => setTimeout(resolve, 500));

          try {
            const freshProjectData = await getCpTeis(numericProjectId);
            const freshBlocks = freshProjectData.blocks || [];
            setProjectData(freshBlocks);

            // Ищем созданный блок по характеристикам

            // Сначала пытаемся найти по точным характеристикам
            let createdBlock = freshBlocks.find(
              (block) =>
                block.floor_count === saveData.floor_count &&
                block.apartments_count === saveData.apartments_count &&
                block.basement_area === saveData.basement_area &&
                block.inf_type_id === saveData.inf_type_id &&
                block.block_id === saveData.block_id
            );

            // Если не найден, ищем по менее строгим критериям
            if (!createdBlock) {
              createdBlock = freshBlocks.find(
                (block) =>
                  block.inf_type_id === saveData.inf_type_id &&
                  block.block_id === saveData.block_id &&
                  Math.abs(block.floor_count - saveData.floor_count) <= 1 &&
                  Math.abs(
                    block.apartments_count - saveData.apartments_count
                  ) <= 1
              );
            }

            // Если все еще не найден, ищем самый новый блок с подходящими характеристиками
            if (!createdBlock) {
              createdBlock = freshBlocks
                .filter(
                  (block) =>
                    block.inf_type_id === saveData.inf_type_id &&
                    block.block_id === saveData.block_id
                )
                .sort((a, b) => b.id - a.id)[0]; // Сортируем по ID в убывающем порядке
            }

            // Если все еще не найден, ищем ЛЮБОЙ блок с подходящими характеристиками
            if (!createdBlock) {
              createdBlock = freshBlocks
                .filter((block) => block.inf_type_id === saveData.inf_type_id)
                .sort((a, b) => b.id - a.id)[0]; // Сортируем по ID в убывающем порядке
            }

            // Если все еще не найден, берем самый новый блок вообще
            if (!createdBlock && freshBlocks.length > 0) {
              createdBlock = freshBlocks.sort((a, b) => b.id - a.id)[0];
            }

            if (createdBlock) {
              // Проверяем, есть ли типовые этажи в созданном блоке
              if (
                !createdBlock.typical_floors ||
                createdBlock.typical_floors.length === 0
              ) {
                console.warn("В созданном блоке отсутствуют типовые этажи!");

                // Попробуем обновить блок, добавив типовые этажи
                try {
                  // Создаем корректные данные для обновления
                  const updateData = {
                    floor_count: parseInt(
                      createdBlock.floor_count || saveData.floor_count || 0
                    ),
                    apartments_count: parseInt(
                      createdBlock.apartments_count ||
                        saveData.apartments_count ||
                        0
                    ),
                    basement_area: parseFloat(
                      createdBlock.basement_area || saveData.basement_area || 0
                    ),
                    total_area: parseFloat(
                      createdBlock.total_area || saveData.total_area || 0
                    ),
                    editing_allowed: true,
                    inf_type_id: parseInt(
                      createdBlock.inf_type?.id ||
                        createdBlock.inf_type_id ||
                        saveData.inf_type_id ||
                        0
                    ),
                    eng_room_ids:
                      createdBlock.eng_rooms?.map((r) => r.id) ||
                      createdBlock.eng_room_ids ||
                      saveData.eng_room_ids ||
                      [],
                    typical_floors: (saveData.typical_floors || []).map(
                      (floor) => ({
                        floor_number_from:
                          parseInt(floor.floor_number_from) || 0,
                        floor_number_to: parseInt(floor.floor_number_to) || 0,
                        area: parseFloat(floor.area) || 0,
                        floors_count: parseInt(floor.floors_count) || 1,
                        total_area: parseFloat(floor.total_area) || 0,
                      })
                    ),
                    typical_floors_area: parseFloat(
                      saveData.typical_floors_area || 0
                    ),
                    typical_floors_count: parseInt(
                      saveData.typical_floors_count || 0
                    ),
                    non_typical_floors: (
                      createdBlock.non_typical_floors ||
                      saveData.non_typical_floors ||
                      []
                    ).map((floor) => ({
                      commercial_area: parseFloat(floor.commercial_area) || 0,
                      other_area: parseFloat(floor.other_area) || 0,
                      floor_number: parseInt(floor.floor_number) || 0,
                      total_area: parseFloat(floor.total_area) || 0,
                    })),
                    // Сервер требует эти поля даже при обновлении
                    project_id: parseInt(
                      createdBlock.project?.id || saveData.project_id || 0
                    ),
                    block_id: parseInt(
                      createdBlock.block?.id || saveData.block_id || 0
                    ),
                  };

                  // Перезагружаем данные после обновления
                  const updatedProjectData = await getCpTeis(numericProjectId);
                  const updatedBlocks = updatedProjectData.blocks || [];
                  setProjectData(updatedBlocks);

                  // Ищем обновленный блок
                  const updatedBlock = updatedBlocks.find(
                    (b) => b.id === createdBlock.id
                  );
                  if (updatedBlock) {
                    createdBlock = updatedBlock;
                  }
                } catch (updateError) {
                  console.error(
                    "Ошибка при обновлении блока с типовыми этажами:",
                    updateError
                  );
                }
              }

              // Обновляем activeBlockTab на реальный ID
              setActiveBlockTab(createdBlock.id);

              // Обновляем blockExtras для нового ID блока
              setBlockExtras((prev) => ({
                ...prev,
                [createdBlock.id]: {
                  coefficient:
                    blockExtras[currentLocalBlock.id]?.coefficient || 0,
                  rate: blockExtras[currentLocalBlock.id]?.rate || 0,
                },
              }));

              // Удаляем старые данные из blockExtras
              setBlockExtras((prev) => {
                const { [currentLocalBlock.id]: removed, ...rest } = prev;
                return rest;
              });

              // Обрабатываем типичные этажи для обновленных данных
              processTypicalFloors(freshBlocks, true);

              // Автоматически сохраняем настройки проекта после успешного создания блока
              try {
                await handleSaveProjectSettings();
              } catch (settingsError) {
                console.error(
                  "Ошибка при автоматическом сохранении настроек:",
                  settingsError
                );
              }

              setSuccessMessage("Блок и настройки проекта успешно сохранены");
              setShowSuccessPopup(true);
              setTimeout(() => setShowSuccessPopup(false), 3000);
              return; // Выходим из функции, так как блок уже обработан
            } else {
              console.warn("Созданный блок не найден в перезагруженных данных");
            }
          } catch (reloadError) {
            console.error(
              "Ошибка при перезагрузке данных проекта:",
              reloadError
            );
          }
        }

        // Если не удалось найти блок, используем старую логику
        // Создаем сохраненный блок с данными из локального блока
        const savedBlock = {
          ...currentLocalBlock,
          id: result?.id || Date.now(), // Используем ID из ответа или временный ID
          is_local: false, // Больше не локальный блок
          // Обновляем данные из saveData
          floor_count: saveData.floor_count,
          apartments_count: saveData.apartments_count,
          basement_area: saveData.basement_area,
          total_area: saveData.total_area,
          inf_type_id: saveData.inf_type_id,
          eng_room_ids: saveData.eng_room_ids,
          typical_floors: saveData.typical_floors,
          typical_floors_area: saveData.typical_floors_area,
          typical_floors_count: saveData.typical_floors_count,
          non_typical_floors: saveData.non_typical_floors,
          // Добавляем данные о коэффициентах и ставках из blockExtras
          coefficient: blockExtras[currentLocalBlock.id]?.coefficient || 0,
          price_per_m2: blockExtras[currentLocalBlock.id]?.rate || 0,
        };

        // Автоматически сохраняем настройки проекта после создания блока
        try {
          await handleSaveProjectSettings();
        } catch (settingsError) {
          console.error(
            "Ошибка при автоматическом сохранении настроек:",
            settingsError
          );
        }

        // НЕ добавляем блок локально, вместо этого перезагружаем данные
        setSuccessMessage("Блок успешно создан, обновляем данные...");
        setShowSuccessPopup(true);
        setTimeout(() => setShowSuccessPopup(false), 3000);
      } else {
        // Если это существующий блок, обновляем его

        // Проверяем, что у нас есть актуальный ID блока
        if (!currentBlockData || !currentBlockData.id) {
          console.error(
            "Не удалось получить актуальный ID блока для обновления"
          );
          throw new Error("Не удалось получить актуальный ID блока");
        }

        // Блок существует в списке проекта, поэтому сразу пытаемся обновить

        // Отправляем данные с нетипичными этажами (API требует это поле)
        const dataToSend = simplifiedData;

        // Попробуем упростить данные - уберем поля, которые могут вызывать проблемы
        const fullData = {
          floor_count: parseInt(simplifiedData.floor_count) || 0,
          apartments_count: parseInt(simplifiedData.apartments_count) || 0,
          basement_area: parseFloat(simplifiedData.basement_area) || 0,
          total_area: parseFloat(simplifiedData.total_area) || 0,
          editing_allowed: simplifiedData.editing_allowed,
          inf_type_id: parseInt(simplifiedData.inf_type_id) || 0,
          project_id: parseInt(simplifiedData.project_id) || 0,
          block_id: parseInt(simplifiedData.block_id) || 0,
          eng_room_ids: simplifiedData.eng_room_ids || [],
          typical_floors: Array.isArray(simplifiedData.typical_floors)
            ? simplifiedData.typical_floors.map((floor) => ({
                floor_number_from: parseInt(floor.floor_number_from) || 0,
                floor_number_to: parseInt(floor.floor_number_to) || 0,
                area: parseFloat(floor.area) || 0,
                floors_count: parseInt(floor.floors_count) || 0,
                total_area: parseFloat(floor.total_area) || 0,
              }))
            : [],
          typical_floors_area:
            parseFloat(simplifiedData.typical_floors_area) || 0,
          typical_floors_count:
            parseInt(simplifiedData.typical_floors_count) || 0,
          non_typical_floors: Array.isArray(simplifiedData.non_typical_floors)
            ? simplifiedData.non_typical_floors.map((floor) => ({
                commercial_area: parseFloat(floor.commercial_area) || 0,
                other_area: parseFloat(floor.other_area) || 0,
                floor_number: parseInt(floor.floor_number) || 0,
                total_area: parseFloat(floor.total_area) || 0,
              }))
            : [],
        };

        // Используем полные данные с floors_count и total_area в typical_floors

        try {
          // Отправляем реальные данные
          // Используем ID CP TEI записи для обновления
          const cpTeiId = currentBlockData.id;

          // Дополнительная проверка ID перед отправкой
          if (
            !cpTeiId ||
            cpTeiId === "local" ||
            cpTeiId === "temp" ||
            cpTeiId < 0 ||
            String(cpTeiId).length > 13
          ) {
            console.error("Некорректный ID блока для обновления:", cpTeiId);
            console.error(
              "Возможно, это временный ID, созданный через Date.now()"
            );
            throw new Error(
              "Некорректный ID блока для обновления. Попробуйте перезагрузить страницу."
            );
          }

          // Принудительно обновляем currentBlockData перед отправкой
          const freshCurrentBlockData = projectData.find(
            (b) => b.id === cpTeiId
          );
          if (
            freshCurrentBlockData &&
            freshCurrentBlockData.id !== currentBlockData.id
          ) {
            // Обновляем ссылку на актуальные данные
            currentBlockData = freshCurrentBlockData;
          }

          // Дополнительная проверка - если блок не найден, возможно он был только что создан
          if (!freshCurrentBlockData) {
            console.warn(
              "Блок не найден в projectData, возможно он был только что создан"
            );
            // Попробуем найти блок по другим критериям
            const alternativeBlock = projectData.find(
              (b) =>
                b.temp_id === cpTeiId ||
                b.local_id === cpTeiId ||
                b.id === activeBlockTab
            );
            if (alternativeBlock) {
              // Обновляем ID для использования
              cpTeiId = alternativeBlock.id;
            }
          }

          // Финальная проверка ID перед отправкой
          if (!cpTeiId || cpTeiId === "local" || cpTeiId === "temp") {
            console.error(
              "Некорректный ID блока после всех проверок:",
              cpTeiId
            );
            throw new Error("Некорректный ID блока для обновления");
          }

          result = await updateCpTei(cpTeiId, fullData);
        } catch (error) {
          console.error("Ошибка при обновлении блока:", error);
          throw error;
        }

        // Перезагружаем данные проекта после обновления
        try {
          const updatedProjectDataResponse = await getCpTeis(numericProjectId);
          const updatedBlocks = updatedProjectDataResponse.blocks || [];
          setProjectData(updatedBlocks);

          // Обновляем версию
          if (
            updatedProjectDataResponse.version !== undefined &&
            updatedProjectDataResponse.version !== null
          ) {
            setVersion(updatedProjectDataResponse.version);
          }

          // Обрабатываем типичные этажи для обновленных данных
          processTypicalFloors(updatedBlocks, true);

          // Обновляем activeBlockTab если текущий блок был удален или изменен
          if (
            activeBlockTab &&
            !updatedBlocks.find((b) => b.id === activeBlockTab)
          ) {
            setActiveBlockTab(null);
          }
        } catch (error) {
          // Если не удалось перезагрузить, обновляем локально
          setProjectData((prev) =>
            prev.map((block) =>
              block.id === currentBlockData.id
                ? {
                    ...block,
                    floor_count: fullData.floor_count,
                    apartments_count: fullData.apartments_count,
                    basement_area: fullData.basement_area,
                    total_area: fullData.total_area,
                    inf_type_id: fullData.inf_type_id,
                    eng_room_ids: fullData.eng_room_ids,
                    typical_floors: fullData.typical_floors,
                    typical_floors_area: fullData.typical_floors_area,
                    typical_floors_count: fullData.typical_floors_count,
                    non_typical_floors: fullData.non_typical_floors,
                  }
                : block
            )
          );

          // Обрабатываем типичные этажи для локально обновленных данных
          const updatedBlocks = projectData.map((block) =>
            block.id === currentBlockData.id
              ? {
                  ...block,
                  floor_count: fullData.floor_count,
                  apartments_count: fullData.apartments_count,
                  basement_area: fullData.basement_area,
                  total_area: fullData.total_area,
                  inf_type_id: fullData.inf_type_id,
                  eng_room_ids: fullData.eng_room_ids,
                  typical_floors: fullData.typical_floors,
                  typical_floors_area: fullData.typical_floors_area,
                  typical_floors_count: fullData.typical_floors_count,
                  non_typical_floors: fullData.non_typical_floors,
                }
              : block
          );
          processTypicalFloors(updatedBlocks, true);
        }

        // Автоматически сохраняем настройки проекта после успешного обновления блока
        try {
          await handleSaveProjectSettings();
        } catch (settingsError) {
          console.error(
            "Ошибка при автоматическом сохранении настроек:",
            settingsError
          );
        }

        setSuccessMessage("Блок и настройки проекта успешно обновлены");
        setShowSuccessPopup(true);
        setTimeout(() => setShowSuccessPopup(false), 3000);
      }

      // Автоматически сохраняем настройки проекта после успешного сохранения блока
      try {
        await handleSaveProjectSettings();
      } catch (settingsError) {
        console.error(
          "Ошибка при автоматическом сохранении настроек:",
          settingsError
        );
        // Не показываем ошибку пользователю, так как основной блок уже сохранен
      }

      // Пересчитываем общую сумму после обновления данных
      setTimeout(() => {
        recalculateTotal();
      }, 0);

      // После успешного сохранения скрываем детали до следующего выбора
      setActiveBlockTab(null);

      // Показываем успешный попап
      setShowSuccessPopup(true);
      setTimeout(() => {
        setShowSuccessPopup(false);
      }, 3000);
    } catch (error) {
      console.error("Ошибка при сохранении блока:", error);

      // Показываем пользователю подробную информацию об ошибке
      let errorMessage = "Ошибка при сохранении блока";

      if (error.message.includes("Некорректный ID блока")) {
        errorMessage =
          "Ошибка: Блок был только что создан. Перезагрузите страницу и попробуйте снова.";
      } else if (error.message.includes("500")) {
        errorMessage =
          "Ошибка сервера (500). Возможные причины:\n" +
          "• Неправильный формат данных\n" +
          "• Отсутствующие обязательные поля\n" +
          "• Проблемы с базой данных\n" +
          "• Временный ID блока (требуется перезагрузка)\n\n" +
          "Детали ошибки: " +
          error.message;
      } else if (error.message.includes("400")) {
        errorMessage = "Ошибка валидации данных (400): " + error.message;
      } else if (error.message.includes("401")) {
        errorMessage =
          "Ошибка авторизации (401). Попробуйте перезайти в систему.";
      } else if (error.message.includes("403")) {
        errorMessage =
          "Ошибка доступа (403). У вас нет прав для выполнения этого действия.";
      } else if (error.message.includes("404")) {
        errorMessage = "Ресурс не найден (404). Возможно, блок был удален.";
      } else {
        errorMessage = "Неизвестная ошибка: " + error.message;
      }

      setSuccessMessage(errorMessage);
      setShowSuccessPopup(true);
      setTimeout(() => {
        setShowSuccessPopup(false);
      }, 5000);
    }
  };

  // Функция для проверки, является ли блок паркингом
  const isParkingBlock = (block) => {
    const blockName = block.block?.name || block.name || "";
    return blockName.toLowerCase().includes("паркинг");
  };

  // Объединяем сохраненные блоки и локальные блоки
  const allBlocksRaw = [...projectData, ...localBlocks];

  // Сортируем блоки так, чтобы паркинг был первым
  const allBlocks = [...allBlocksRaw].sort((a, b) => {
    const aIsParking = isParkingBlock(a);
    const bIsParking = isParkingBlock(b);
    if (aIsParking && !bIsParking) return -1;
    if (!aIsParking && bIsParking) return 1;
    return 0;
  });

  // Получаем блок паркинга (если есть)
  const parkingBlock = allBlocks.find(isParkingBlock);
  const hasParking = !!parkingBlock;

  // Блоки без паркинга для подсчета количества
  const blocksWithoutParking = allBlocks.filter(
    (block) => !isParkingBlock(block)
  );

  const currentBlock = allBlocks.find((b) => b.id === activeBlockTab);

  // Функция для принудительного обновления верхней таблицы
  // Состояние для принудительного обновления верхней таблицы
  const [topTableUpdateKey, setTopTableUpdateKey] = useState(0);

  const forceUpdateTopTable = () => {
    // Принудительно обновляем состояние, чтобы перерисовать верхнюю таблицу
    setTopTableUpdateKey((prev) => prev + 1);
  };

  // Функция для получения актуальных данных блока для отображения в таблице
  const getBlockDisplayData = (block) => {
    // Получаем данные из формы для текущего блока
    const formDataForBlock = formData[block.id];
    const isParking = isParkingBlock(block);

    // Если есть данные формы для этого блока, используем их
    if (formDataForBlock) {
      // Для паркинга используем total_area напрямую
      if (isParking && formDataForBlock.total_area !== undefined) {
        return {
          floor_count:
            formDataForBlock.floor_count !== undefined
              ? formDataForBlock.floor_count
              : block.floor_count,
          total_area: formDataForBlock.total_area,
          inf_type:
            formDataForBlock.inf_type_id !== undefined
              ? infoTypes.find(
                  (type) => type.id === formDataForBlock.inf_type_id
                )
              : block.inf_type,
        };
      }

      // Для обычных блоков используем только результат от API (lastCalculatedTotal)
      // Вся калькуляция должна выполняться на бэкенде через API calculateTotalArea
      const calculatedTotalArea = lastCalculatedTotal[block.id];

      return {
        floor_count:
          formDataForBlock.floor_count !== undefined
            ? formDataForBlock.floor_count
            : block.floor_count,
        total_area:
          calculatedTotalArea !== undefined
            ? calculatedTotalArea
            : formDataForBlock.total_area || block.total_area || 0,
        inf_type:
          formDataForBlock.inf_type_id !== undefined
            ? infoTypes.find((type) => type.id === formDataForBlock.inf_type_id)
            : block.inf_type,
      };
    }

    // Иначе используем данные из блока
    return {
      floor_count: block.floor_count,
      total_area: block.total_area,
      inf_type: block.inf_type,
    };
  };

  // Показываем прелоадер пока загружаются данные
  if (isLoadingProjectData || isLoadingInfoTypes || isLoadingEngrooms) {
    return <Preloader />;
  }

  // Показываем прелоадер при сохранении всех блоков
  if (isSavingAllBlocks) {
    return (
      <div style={{ position: "relative", minHeight: "400px" }}>
        <Preloader />
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            marginTop: "60px",
            textAlign: "center",
            fontSize: "16px",
            color: "#666",
          }}
        >
          Сохранение блоков...
        </div>
      </div>
    );
  }

  // Показываем сообщение об ошибке 500
  if (hasServerError) {
    return (
      <div
        className="initial-data-scope"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          className="error-message"
          style={{
            textAlign: "center",
            padding: "50px 20px",
            fontSize: "18px",
            color: "#d32f2f",
            backgroundColor: "#ffebee",
            borderRadius: "8px",
            border: "1px solid #f44336",
          }}
        >
          <h3>Ошибка 500</h3>
          <p>Нет данных</p>
        </div>
      </div>
    );
  }

  return (
    <div className="initial-data-scope">
      {/* Предупреждение о блокировке редактирования */}
      {(editingMessage ||
        cpApprovalStatus === "approved" ||
        cpApprovalStatus === "согласован" ||
        peApprovalStatus === "approved" ||
        peApprovalStatus === "согласован") && (
        <div
          style={{
            background: "#fff8f1",
            border: "1px solid #ffe0b2",
            borderRadius: "8px",
            padding: "12px 16px",
            marginBottom: "20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flex: 1,
            }}
          >
            <span style={{ fontSize: "24px" }}>⚠️</span>
            <span style={{ color: "#b26a00", fontSize: "14px" }}>
              {editingMessage ||
                (cpApprovalStatus === "approved" ||
                cpApprovalStatus === "согласован" ||
                peApprovalStatus === "approved" ||
                peApprovalStatus === "согласован"
                  ? "ТЭП находится на этапе согласования, поэтому редактирование заблокировано"
                  : "")}
            </span>
          </div>
          {(cpApprovalStatus === "approved" ||
            cpApprovalStatus === "согласован" ||
            peApprovalStatus === "approved" ||
            peApprovalStatus === "согласован") && (
            <button
              onClick={async () => {
                if (
                  !window.confirm(
                    "Вы уверены, что хотите создать копию? Будет создана новая версия ТЭП, Сметы и КП без согласований."
                  )
                ) {
                  return;
                }

                try {
                  setIsCreatingCopy(true);
                  await restoreVersion(numericProjectId, null);
                  // Перезагружаем страницу после создания копии
                  setTimeout(() => {
                    window.location.reload();
                  }, 500);
                } catch (error) {
                  console.error("Ошибка при создании копии:", error);
                  alert(`Ошибка при создании копии: ${error.message}`);
                } finally {
                  setIsCreatingCopy(false);
                }
              }}
              disabled={isCreatingCopy}
              style={{
                padding: "8px 16px",
                background: "#1976d2",
                color: "white",
                border: "none",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: 500,
                cursor: isCreatingCopy ? "not-allowed" : "pointer",
                opacity: isCreatingCopy ? 0.6 : 1,
                whiteSpace: "nowrap",
              }}
            >
              {isCreatingCopy ? "Создание копии..." : "Сделать копию"}
            </button>
          )}
        </div>
      )}
      <div className="summary-section">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            marginBottom: "16px",
          }}
        >
          <h2 className="section-title" style={{ margin: 0 }}>
            Исходные технико-экономические показатели
          </h2>
          {version !== null && version !== undefined && (
            <span className="initial-data-version-badge">Версия {version}</span>
          )}
        </div>
        <div className="summary-main">
          <div className="summary-blocks-list">
            <div className="summary-header">
              <div className="h-empty"></div>
              <div className="h-name">Наименование</div>
              <div className="h-floors">Этажность</div>
              <div className="h-area">Площадь</div>
              <div className="h-type">Тип</div>
              <div className="h-coeff">Коэффициент</div>
              <div className="h-rate">Ставка</div>
            </div>
            {isLoadingProjectData ? (
              <div>Загрузка данных проекта...</div>
            ) : allBlocks.length > 0 ? (
              allBlocks.map((block) => (
                <div
                  key={`${block.id}-${topTableUpdateKey}`}
                  className={`summary-block-item ${
                    block.is_local ? "local-block" : ""
                  }`}
                >
                  <div className="block-icon-wrap">
                    <img
                      src={isParkingBlock(block) ? ParkingIcon : VectorIcon}
                      alt={
                        isParkingBlock(block) ? "parking icon" : "block icon"
                      }
                      className="block-icon"
                    />
                    {block.is_local && (
                      <span className="local-indicator">*</span>
                    )}
                  </div>
                  <div className="block-name">
                    {block.block?.name || "Неизвестный блок"}
                  </div>
                  <div className="block-floors">
                    {(() => {
                      const displayData = getBlockDisplayData(block);
                      return `${displayData.floor_count} этажей`;
                    })()}
                  </div>
                  <div className="block-area">
                    {(() => {
                      const displayData = getBlockDisplayData(block);
                      return `${smartRound(displayData.total_area || 0)} м²`;
                    })()}
                  </div>
                  <div
                    className={`block-type ${(() => {
                      const displayData = getBlockDisplayData(block);
                      return displayData.inf_type?.name === "привязка"
                        ? "type-link"
                        : displayData.inf_type?.name === "зеркало"
                        ? "type-mirror"
                        : "type-base";
                    })()}`}
                  >
                    {(() => {
                      const displayData = getBlockDisplayData(block);
                      return displayData.inf_type?.name || "Не выбран";
                    })()}
                  </div>
                  <div className="block-coeff">
                    <span className="coefficient-display">
                      {typeof (blockExtras[block.id]?.coefficient ?? 0) ===
                      "number"
                        ? blockExtras[block.id]?.coefficient ?? 0
                        : Number(blockExtras[block.id]?.coefficient ?? 0)}
                    </span>
                  </div>
                  <div className="block-rate">
                    <InlineEditable
                      value={
                        typeof (blockExtras[block.id]?.rate ?? 0) === "number"
                          ? blockExtras[block.id]?.rate ?? 0
                          : Number(blockExtras[block.id]?.rate ?? 0)
                      }
                      onSave={(v) => saveEditRate(block.id, v)}
                      width={80}
                      isEditable={effectiveIsEditable}
                      inputProps={{ type: "number", step: "100", min: "0" }}
                    />
                  </div>
                </div>
              ))
            ) : (
              <div>Нет данных о блоках</div>
            )}
          </div>
          <div className="summary-center-panel" style={{ gap: 28 }}>
            <div className="center-item center-top">
              <img src={PyramidIcon} alt="plot" className="center-icon" />
              <div className="center-row">
                <div className="center-label">Площадь участка:</div>
                <div className="center-value">
                  {plotAreaHa ? `${plotAreaHa} га` : "-"}
                </div>
              </div>
            </div>
            <div className="center-item">
              <img
                src={BuildingIcon}
                alt="blocks"
                className="center-icon small"
              />
              <div className="center-row">
                <div className="center-label">Количество блоков:</div>
                <div className="center-value">
                  {blocksWithoutParking.length}
                </div>
              </div>
            </div>
            {hasParking && (
              <div className="center-item">
                <img
                  src={ParkingIcon}
                  alt="parking"
                  className="center-icon small"
                />
                <div className="center-row">
                  <div className="center-label">Паркинг:</div>
                  <div className="center-value">Есть</div>
                </div>
              </div>
            )}
            {!hasParking && (
              <div className="center-item">
                <img
                  src={ParkingIcon}
                  alt="parking"
                  className="center-icon small"
                />
                <div className="center-row">
                  <div className="center-label">Паркинг:</div>
                  <div className="center-value">Нет</div>
                </div>
              </div>
            )}
            <div className="center-item">
              <img src={CubesIcon} alt="area" className="center-icon small" />
              <div className="center-row">
                <div className="center-label">Общая площадь</div>
                <div className="center-value">
                  {smartRound(
                    allBlocks.reduce((sum, b) => {
                      const displayData = getBlockDisplayData(b);
                      // Используем рассчитанную площадь из формы или данные блока
                      const blockArea = displayData.total_area || 0;
                      return sum + blockArea;
                    }, 0)
                  )}{" "}
                  м²
                </div>
              </div>
            </div>
          </div>
          <div className="schema-block side-block">
            <div className="schema-title">Схема блокировки</div>
            {blocksSchemaImageUrl ? (
              <div className="schema-image-container">
                <div style={{ position: "relative" }}>
                  <img
                    src={blocksSchemaImageUrl}
                    alt="Схема блокировки"
                    style={{
                      width: "350px",
                      maxWidth: "100%",
                      height: "auto",
                      borderRadius: "8px",
                      border: "1px solid #E6E8EC",
                      cursor: "pointer",
                    }}
                    onClick={() => setShowImagePopup(true)}
                    title="Нажмите для увеличения"
                    onLoad={() => {}}
                    onError={(e) => {
                      // Показываем сообщение об ошибке
                      setSuccessMessage(
                        "Ошибка загрузки изображения: файл не найден"
                      );
                      setShowSuccessPopup(true);
                      setTimeout(() => setShowSuccessPopup(false), 3000);
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowImagePopup(true)}
                    style={{
                      position: "absolute",
                      top: "8px",
                      right: "8px",
                      padding: "4px 8px",
                      fontSize: "12px",
                      backgroundColor: "rgba(0, 0, 0, 0.7)",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                    }}
                    title="Открыть на полный экран"
                  >
                    🔍
                  </button>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    marginTop: "8px",
                    justifyContent: "center",
                  }}
                >
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        // TODO: Добавить API эндпоинт для удаления изображения
                        // await deleteProjectImage(numericProjectId);
                        setBlocksSchemaImageUrl("");
                        setSuccessMessage("Схема удалена");
                        setShowSuccessPopup(true);
                        setTimeout(() => setShowSuccessPopup(false), 2000);
                      } catch (error) {
                        console.error("Ошибка при удалении схемы:", error);
                        setSuccessMessage("Ошибка при удалении схемы");
                        setShowSuccessPopup(true);
                        setTimeout(() => setShowSuccessPopup(false), 2000);
                      }
                    }}
                    disabled={!effectiveIsEditable || isUploadingImage}
                    style={{
                      padding: "4px 8px",
                      fontSize: "12px",
                      backgroundColor: "#dc3545",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor:
                        !isEditable || isUploadingImage
                          ? "not-allowed"
                          : "pointer",
                      opacity: !isEditable || isUploadingImage ? 0.6 : 1,
                    }}
                  >
                    Удалить
                  </button>
                </div>
              </div>
            ) : (
              <div className="upload-box">
                <input
                  id="scheme-upload"
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml"
                  disabled={!effectiveIsEditable || isUploadingImage}
                  onChange={async (e) => {
                    if (!effectiveIsEditable) return; // Не загружаем если редактирование заблокировано
                    const file = e.target.files && e.target.files[0];
                    if (!file) return;

                    setIsUploadingImage(true);
                    try {
                      // Показываем сообщение о начале загрузки
                      const action = blocksSchemaImageUrl
                        ? "замены"
                        : "загрузки";
                      setSuccessMessage(
                        `${
                          action.charAt(0).toUpperCase() + action.slice(1)
                        } файла: ${file.name}...`
                      );
                      setShowSuccessPopup(true);

                      // Загружаем файл через API (перезаписывает существующее изображение)
                      const result = await uploadProjectImage(
                        numericProjectId,
                        file
                      );

                      // Обновляем URL изображения с полученным именем файла
                      if (result.image_name) {
                        // Сохраняем URL изображения для передачи при сохранении настроек
                        // На локальной машине используем другой путь
                        const isLocalhost =
                          window.location.hostname === "localhost" ||
                          window.location.hostname === "127.0.0.1";
                        let imageUrl;
                        if (isLocalhost) {
                          // На локальной машине используем старый путь
                          imageUrl = `${getAPIURL()}images/blocks_schema/${
                            result.image_name
                          }`;
                        } else {
                          // На серверах используем новый путь
                          // Проверяем, является ли это тестовым окружением
                          const isTestEnv =
                            process.env.REACT_APP_ENV === "test";
                          if (isTestEnv) {
                            // Для тестового окружения используем полный URL без дублирования
                            imageUrl = `https://test.portal.akmolpro.kz/api/images/blocks_schema/${result.image_name}`;
                          } else {
                            // На продакшн серверах используем новый путь
                            const isProdEnv =
                              process.env.REACT_APP_ENV === "prod";
                            if (isProdEnv) {
                              // Для продакшн окружения используем полный URL без дублирования
                              imageUrl = `https://portal.akmolpro.kz/api/images/blocks_schema/${result.image_name}`;
                            } else {
                              // Для других серверов используем новый путь
                              imageUrl = `${getAPIURL()}project_estimates/cp_teis/${numericProjectId}/images/${
                                result.image_name
                              }`;
                            }
                          }
                        }
                        setBlocksSchemaImageUrl(imageUrl);

                        setSuccessMessage(
                          `Файл успешно ${
                            action === "замены" ? "заменен" : "загружен"
                          }: ${result.image_name}`
                        );
                      } else {
                        setSuccessMessage(
                          `Файл успешно ${
                            action === "замены" ? "заменен" : "загружен"
                          }: ${file.name}`
                        );
                      }
                    } catch (error) {
                      console.error("Ошибка при загрузке файла:", error);
                      setSuccessMessage(
                        `Ошибка при загрузке файла: ${error.message}`
                      );
                    } finally {
                      setIsUploadingImage(false);
                      setTimeout(() => setShowSuccessPopup(false), 3000);
                      // Очищаем input, чтобы можно было загрузить тот же файл повторно
                      e.target.value = "";
                    }
                  }}
                />
                <label
                  htmlFor="scheme-upload"
                  className="upload-label"
                  style={{
                    opacity: isUploadingImage ? 0.6 : 1,
                    cursor: isUploadingImage ? "not-allowed" : "pointer",
                  }}
                >
                  {isUploadingImage ? "Загрузка..." : "Выбрать файл"}
                </label>
                <div className="upload-hint">
                  Загрузите файл в формате PNG, JPEG, SVG
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="toggles-row">
          <div className="toggle-item">
            <span>Применить коэффициент этажности</span>
            <label className="switch">
              <input
                type="checkbox"
                checked={applyFloorCoefficient}
                onChange={(e) => setApplyFloorCoefficient(e.target.checked)}
                disabled={!effectiveIsEditable}
              />
              <span className="slider"></span>
            </label>
          </div>
          <div className="toggle-item">
            <span>Применить коэффициент ускорения</span>
            <label className="switch">
              <input
                type="checkbox"
                checked={applyAccelerationCoefficient}
                onChange={(e) =>
                  setApplyAccelerationCoefficient(e.target.checked)
                }
                disabled={!effectiveIsEditable}
              />
              <span className="slider"></span>
            </label>
          </div>
          <div className="toggle-item">
            <span>BIM</span>
            <label className="switch">
              <input
                type="checkbox"
                checked={applyBimCoefficient}
                onChange={(e) => setApplyBimCoefficient(e.target.checked)}
                disabled={!effectiveIsEditable}
              />
              <span className="slider"></span>
            </label>
          </div>
          <div className="toggle-item">
            <span>Введите площадь участка* (га)</span>
            <InlineEditable
              value={plotAreaHa}
              onSave={(v) => setPlotAreaHa(v)}
              width={45}
              isEditable={effectiveIsEditable}
              inputProps={{
                type: "number",
                step: "0.001",
                min: "0",
                inputMode: "decimal",
              }}
            />
          </div>
          <div className="toggle-item">
            <span>Ставка за разработку сметной документации</span>
            <InlineEditable
              value={smetaDocumentationPercent}
              onSave={(v) => setSmetaDocumentationPercent(v)}
              width={45}
              isEditable={effectiveIsEditable}
              inputProps={{
                type: "number",
                step: "0.01",
                min: "0",
                inputMode: "decimal",
              }}
            />
          </div>
          <div style={{ marginLeft: "auto" }}>
            <button
              type="button"
              className="btn-save-settings"
              onClick={handleSaveProjectSettings}
              disabled={!effectiveIsEditable}
            >
              Сохранить настройки
            </button>
          </div>
        </div>
      </div>

      {(!projectNotFound || allBlocks.length > 0) && (
        <div className="details-section">
          <h2 className="section-title">Детализация по блокам</h2>
          <div className="details-tabs">
            {isLoadingProjectData ? (
              <div>Загрузка...</div>
            ) : allBlocks.length > 0 ? (
              allBlocks.map((block) => (
                <button
                  key={block.id}
                  className={`details-tab-btn ${
                    activeBlockTab === block.id ? "active" : ""
                  } ${block.is_local ? "local-tab" : ""}`}
                  onClick={() => {
                    // Сохраняем данные текущего блока перед переключением
                    if (
                      activeBlockTab !== null &&
                      activeBlockTab !== block.id
                    ) {
                      // Данные автоматически сохраняются через onFormDataChange в BlockDetailForm
                      // Просто переключаемся на новый блок
                    }
                    setActiveBlockTab(block.id);
                  }}
                >
                  {block.block?.name || "Неизвестный блок"}
                  {block.is_local && <span className="local-indicator">*</span>}
                </button>
              ))
            ) : (
              <div>Нет блоков</div>
            )}
            <button
              className="add-block-btn"
              onClick={handleAddBlockClick}
              disabled={!effectiveIsEditable}
              style={{
                cursor: !isEditable ? "not-allowed" : "pointer",
                opacity: !isEditable ? 0.6 : 1,
              }}
            >
              + Добавить здание
            </button>
          </div>
          {activeBlockTab !== null && (
            <div className="details-content details-split">
              <div className="details-form details-form-block">
                <BlockDetailForm
                  blockId={activeBlockTab}
                  blockName={currentBlock?.block?.name}
                  onDeleteClick={handleDeleteClick}
                  total={total}
                  onCalcClick={handleCalcClick}
                  onAddTypicalClick={handleAddTypicalClick}
                  typicalVisible={true}
                  infoTypes={infoTypes}
                  engrooms={engrooms}
                  blockData={currentBlock}
                  onFormDataChange={handleFormDataChange}
                  onAutoCalc={handleAutoCalc}
                  currentFormData={formData[activeBlockTab] || {}}
                  isEditable={effectiveIsEditable}
                  isParking={
                    currentBlock ? isParkingBlock(currentBlock) : false
                  }
                />
              </div>
              <div
                className="calculator-block side-block"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 340,
                }}
              >
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                  }}
                >
                  {(() => {
                    // Сохраняем ID текущего блока ДО map, чтобы избежать проблем с замыканием
                    const currentBlockId = activeBlockTab;

                    // Вычисляем минимальный номер для типовых этажей на основе нетипичных этажей один раз
                    const nonTypicalFloors =
                      formData[currentBlockId]?.non_typical_floors || [];
                    const maxNonTypicalFloor =
                      nonTypicalFloors.length > 0
                        ? Math.max(
                            ...nonTypicalFloors.map(
                              (floor) =>
                                floor.floorNumber || floor.floor_number || 0
                            )
                          )
                        : 0;
                    const minTypicalFloor = Math.max(2, maxNonTypicalFloor + 1);

                    // Получаем типовые этажи для текущего блока
                    const currentTypicalFloors =
                      blockTypicalFloors[currentBlockId] || [];

                    return currentTypicalFloors.map((form, idx) => {
                      return (
                        <TypicalFloorsForm
                          key={form.id}
                          typicalStart={form.typicalStart}
                          setTypicalStart={(val) => {
                            setBlockTypicalFloors((prev) => {
                              const updated = {
                                ...prev,
                                [currentBlockId]: (
                                  prev[currentBlockId] || []
                                ).map((f) =>
                                  f.id === form.id
                                    ? { ...f, typicalStart: val }
                                    : f
                                ),
                              };
                              return updated;
                            });
                          }}
                          typicalEnd={form.typicalEnd}
                          setTypicalEnd={(val) =>
                            setBlockTypicalFloors((prev) => ({
                              ...prev,
                              [currentBlockId]: (
                                prev[currentBlockId] || []
                              ).map((f) =>
                                f.id === form.id ? { ...f, typicalEnd: val } : f
                              ),
                            }))
                          }
                          typicalArea={form.typicalArea}
                          setTypicalArea={(val) =>
                            setBlockTypicalFloors((prev) => ({
                              ...prev,
                              [currentBlockId]: (
                                prev[currentBlockId] || []
                              ).map((f) =>
                                f.id === form.id
                                  ? { ...f, typicalArea: val }
                                  : f
                              ),
                            }))
                          }
                          oneFloorArea={form.oneFloorArea}
                          setOneFloorArea={(val) => {
                            setBlockTypicalFloors((prev) => {
                              const updated = {
                                ...prev,
                                [currentBlockId]: (
                                  prev[currentBlockId] || []
                                ).map((f) =>
                                  f.id === form.id
                                    ? { ...f, oneFloorArea: val }
                                    : f
                                ),
                              };
                              return updated;
                            });
                          }}
                          typicalCount={form.typicalCount}
                          setTypicalCount={(val) =>
                            setBlockTypicalFloors((prev) => ({
                              ...prev,
                              [currentBlockId]: (
                                prev[currentBlockId] || []
                              ).map((f) =>
                                f.id === form.id
                                  ? { ...f, typicalCount: val }
                                  : f
                              ),
                            }))
                          }
                          onCalcClick={handleCalcClick}
                          onTypicalCalcClick={(updatedForm) =>
                            handleTypicalFloorsCalculation(
                              updatedForm,
                              currentBlockId
                            )
                          }
                          onClose={() =>
                            handleRemoveTypicalForm(form.id, currentBlockId)
                          }
                          form={form}
                          isEditable={effectiveIsEditable}
                          maxFloorCount={
                            formData[activeBlockTab]?.floor_count ||
                            currentBlock?.floor_count ||
                            null
                          }
                          minTypicalFloor={minTypicalFloor}
                        />
                      );
                    });
                  })()}
                  {currentBlock && !isParkingBlock(currentBlock) && (
                    <button
                      type="button"
                      className="btn-add-typical"
                      style={{ marginTop: 24, width: "300px" }}
                      onClick={handleAddTypicalClick}
                      disabled={!effectiveIsEditable}
                    >
                      + Добавить типовые этажи
                    </button>
                  )}
                </div>
                {currentBlock && currentBlock.block && (
                  <div className="total-group" style={{ marginTop: 24 }}>
                    <span className="total-label">
                      Общая площадь "
                      {currentBlock.block?.name || "Неизвестный блок"}"
                    </span>
                    <span className="total-value">
                      {(() => {
                        const displayData = getBlockDisplayData(currentBlock);
                        return smartRound(displayData.total_area || 0);
                      })()}{" "}
                      м²
                    </span>
                  </div>
                )}
                {/* <button
              type="button"
              className="btn-calc"
              style={{ marginTop: 24 }}
              onClick={handleCalcClick}
            >
              Посчитать
            </button> */}
              </div>
            </div>
          )}
          {activeBlockTab !== null && (
            <div
              className="footer-actions"
              style={{
                justifyContent: "flex-end",
                marginTop: 16,
                display: "flex",
                gap: 16,
              }}
            >
              {currentBlock && (
                <button
                  type="button"
                  className="btn-delete"
                  onClick={() => handleDeleteClick(currentBlock?.block?.name)}
                  disabled={!effectiveIsEditable}
                >
                  Удалить
                </button>
              )}
              {localBlocks.length > 1 ? (
                <button
                  type="button"
                  className="btn-save-block"
                  onClick={handleSaveAllBlocks}
                  disabled={!effectiveIsEditable || isSavingAllBlocks}
                  style={{
                    backgroundColor: "#2A5E38",
                    color: "white",
                    opacity: isSavingAllBlocks ? 0.6 : 1,
                    cursor: isSavingAllBlocks ? "wait" : "pointer",
                  }}
                >
                  {isSavingAllBlocks ? (
                    <>
                      <span style={{ marginRight: "8px" }}>⏳</span>
                      Сохранение...
                    </>
                  ) : (
                    `Сохранить все (${localBlocks.length})`
                  )}
                </button>
              ) : (
                <button
                  type="button"
                  className="btn-save-block"
                  onClick={handleSaveBlock}
                  disabled={!effectiveIsEditable}
                >
                  Сохранить
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Блок для проектов без данных - показываем только кнопку добавления */}
      {projectNotFound && allBlocks.length === 0 && (
        <div className="details-section">
          <h2 className="section-title">Детализация по блокам</h2>
          <div
            style={{
              textAlign: "center",
              padding: "40px 20px",
              backgroundColor: "#f8f9fa",
              borderRadius: "8px",
              border: "2px dashed #dee2e6",
            }}
          >
            <div
              style={{
                fontSize: "18px",
                color: "#6c757d",
                marginBottom: "20px",
                fontWeight: "500",
              }}
            >
              Для этого проекта еще не созданы технико-экономические показатели
            </div>
            <div
              style={{
                fontSize: "14px",
                color: "#6c757d",
                marginBottom: "30px",
              }}
            >
              Начните с добавления первого здания
            </div>
            <button
              className="add-block-btn"
              onClick={handleAddBlockClick}
              disabled={!effectiveIsEditable}
              style={{
                fontSize: "16px",
                padding: "12px 24px",
                backgroundColor: "#2A5E38",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: !isEditable ? "not-allowed" : "pointer",
                fontWeight: "500",
                opacity: !isEditable ? 0.6 : 1,
              }}
            >
              + Добавить первое здание
            </button>
          </div>
        </div>
      )}

      {showDeletePopup && (
        <div className="popup-overlay">
          <div className="popup">
            <p
              className="popup-text"
              style={{ fontWeight: 600, fontSize: 18, marginBottom: 12 }}
            >
              Вы уверены, что хотите удалить блок "{deletedBlockName}"?
            </p>
            <p
              className="popup-text"
              style={{ color: "#6c757d", marginBottom: 24 }}
            >
              Все данные, введённые в этом блоке, будут удалены без возможности
              восстановления.
            </p>
            <div style={{ display: "flex", gap: 16, justifyContent: "center" }}>
              <button
                className="btn-save"
                style={{
                  background: "#fff",
                  color: "#2A5E38",
                  border: "1.5px solid #2A5E38",
                }}
                onClick={handleCancelDelete}
              >
                Отмена
              </button>
              <button
                className="btn-delete"
                style={{ minWidth: 120 }}
                onClick={handleConfirmDelete}
              >
                Удалить
              </button>
            </div>
          </div>
        </div>
      )}
      {showAddBlockPopup && (
        <div className="popup-overlay">
          <div className="popup">
            <p
              className="popup-text"
              style={{ fontWeight: 600, fontSize: 18, marginBottom: 18 }}
            >
              Добавление нового блока
            </p>
            <div style={{ marginBottom: 24 }}>
              <label
                style={{
                  display: "block",
                  marginBottom: 8,
                  color: "#1A2343",
                  fontWeight: 500,
                }}
              >
                Выберите блоки из списка или создайте новый*
              </label>
              {blocksList.length === 0 && !isLoadingBlocks && (
                <div
                  style={{
                    padding: "8px 0",
                    color: "#6c757d",
                    fontSize: 13,
                  }}
                >
                  Все доступные блоки уже добавлены. Выберите «+ Создать новый блок» или создайте новый.
                </div>
              )}
              <Select
                  isMulti
                  options={[CREATE_NEW_BLOCK_OPTION, ...blocksList]}
                  value={
                    isCreatingNewBlock
                      ? [CREATE_NEW_BLOCK_OPTION]
                      : blocksList.filter((b) =>
                          selectedBlocks.includes(b.value)
                        )
                  }
                  onChange={(selectedOptions) => {
                    const selected = selectedOptions || [];
                    const hasCreateNew = selected.some(
                      (opt) => opt.value === CREATE_NEW_BLOCK_OPTION.value
                    );
                    if (hasCreateNew) {
                      setSelectedBlocks([CREATE_NEW_BLOCK_OPTION.value]);
                      setIsCreatingNewBlock(true);
                      setNewBlockName("");
                      setNewBlockNameError("");
                    } else {
                      setSelectedBlocks(selected.map((opt) => opt.value));
                      setIsCreatingNewBlock(false);
                      setNewBlockName("");
                      setNewBlockNameError("");
                    }
                  }}
                  isLoading={isLoadingBlocks}
                  isDisabled={!effectiveIsEditable}
                  placeholder="Начните вводить или выберите из списка..."
                  noOptionsMessage={() => "Блоки не найдены"}
                  closeMenuOnSelect={false}
                  styles={{
                    container: (base) => ({ ...base, minWidth: 260 }),
                    control: (base) => ({
                      ...base,
                      minHeight: "40px",
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: "#E8E8F8",
                      borderRadius: "4px",
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: "#333",
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      "&:hover": {
                        backgroundColor: "#2A5E38",
                        color: "white",
                      },
                    }),
                  }}
                />
            </div>
            {isCreatingNewBlock && (
              <div style={{ marginBottom: 24 }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: 8,
                    color: "#1A2343",
                    fontWeight: 500,
                  }}
                >
                  Название нового блока*
                </label>
                <input
                  type="text"
                  value={newBlockName}
                  onChange={handleNewBlockNameChange}
                  placeholder="Введите название..."
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: newBlockNameError
                      ? "1px solid #dc3545"
                      : "1px solid #E0E0E0",
                    borderRadius: 8,
                    fontSize: 14,
                    boxSizing: "border-box",
                  }}
                  autoFocus
                />
                {newBlockNameError && (
                  <div
                    style={{
                      color: "#dc3545",
                      fontSize: 12,
                      marginTop: 4,
                    }}
                  >
                    {newBlockNameError}
                  </div>
                )}
              </div>
            )}
            <div style={{ display: "flex", gap: 16, justifyContent: "center" }}>
              <button
                className="btn-save"
                style={{
                  background: "#fff",
                  color: "#2A5E38",
                  border: "1.5px solid #2A5E38",
                }}
                onClick={handleCancelAddBlock}
                disabled={isSubmittingAddBlock}
              >
                Отмена
              </button>
              <button
                className="btn-save"
                style={{ minWidth: 120 }}
                onClick={handleConfirmAddBlock}
                disabled={
                  !effectiveIsEditable ||
                  isSubmittingAddBlock ||
                  (isCreatingNewBlock
                    ? !newBlockName.trim() || !!newBlockNameError
                    : selectedBlocks.length === 0 ||
                      (selectedBlocks.length === 1 &&
                        selectedBlocks[0] === CREATE_NEW_BLOCK_OPTION.value))
                }
              >
                {isSubmittingAddBlock ? "Создание…" : "Добавить"}
              </button>
            </div>
          </div>
        </div>
      )}
      {showSuccessPopup && (
        <div className="popup-overlay">
          <div className="popup">
            <div className="popup-icon"></div>
            <p className="popup-text">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Попап для отображения изображения на полный экран */}
      {showImagePopup && (
        <div
          className="popup-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => setShowImagePopup(false)}
        >
          <div
            style={{
              position: "relative",
              maxWidth: "90vw",
              maxHeight: "90vh",
            }}
          >
            <button
              type="button"
              onClick={() => setShowImagePopup(false)}
              style={{
                position: "absolute",
                top: "-40px",
                right: "0",
                background: "none",
                border: "none",
                color: "white",
                fontSize: "24px",
                cursor: "pointer",
                padding: "8px",
              }}
              title="Закрыть"
            >
              ✕
            </button>
            <img
              src={blocksSchemaImageUrl}
              alt="Схема блокировки"
              style={{
                maxWidth: "90vw",
                maxHeight: "90vh",
                width: "auto",
                height: "auto",
                objectFit: "contain",
                borderRadius: "8px",
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default InitialData;
